import { Fragment, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePermissions } from "@/contexts/PermissionsContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import SupersedeAppsModal from "@/components/SupersedeAppsModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Package,
  Search,
  LogOut,
  Settings,
  Download,
  Upload,
  Trash2,
  Eye,
  RefreshCw,
  Loader2,
  Check,
  CheckCircle,
  X,
  XCircle,
  Clock,
  AlertCircle,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CornerDownRight,
  Link2,
  Plus,
  Users,
  Minus,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Monitor,
  Book,
  Rocket,
  ArrowUpCircle,
  Bell,
  ToggleLeft,
  ToggleRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Ticket,
  Archive,
} from "lucide-react";

export default function PackagesPage() {
  const { user, logout, getAccessToken, demoMode } = useAuth();
  const { canManageApps, canDeployPackages } = usePermissions();

  // Helper to get auth headers for API requests
  const getAuthHeaders = useCallback(async () => {
    if (demoMode) return {};
    try {
      const token = await getAccessToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
      console.error("Failed to get access token:", error);
      return {};
    }
  }, [getAccessToken, demoMode]);

  // State
  const [packages, setPackages] = useState([]);
  // Parent package ids whose dependency rows are expanded
  const [expandedDependencies, setExpandedDependencies] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Sort: { key: 'app_name'|'version'|'status'|'created_at', dir: 'asc'|'desc' }
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  
  // Modal state
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [packageLogs, setPackageLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [deletePackageId, setDeletePackageId] = useState(null);
  const [deleteIntuneApp, setDeleteIntuneApp] = useState(false);
  const [deleteIntuneGroups, setDeleteIntuneGroups] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Retire from Intune (app housekeeping): the version being retired, the
  // server's preview of what will change, and request state.
  const [retirePackage, setRetirePackage] = useState(null);
  const [retirePreview, setRetirePreview] = useState(null);
  const [retirePreviewError, setRetirePreviewError] = useState(null);
  const [isRetiring, setIsRetiring] = useState(false);

  // Housekeeping suggestions (versions ready to retire / delete from Intune)
  // and each package's latest daily install reading.
  const [housekeeping, setHousekeeping] = useState({ suggestions: [], latest_installs: {} });
  const [housekeepingOpen, setHousekeepingOpen] = useState(false);

  // Delete a retired version from Intune (ALICE keeps its record).
  const [deleteIntunePkg, setDeleteIntunePkg] = useState(null);
  const [deleteIntunePreview, setDeleteIntunePreview] = useState(null);
  const [deleteIntuneError, setDeleteIntuneError] = useState(null);
  const [deleteIntuneConfirmed, setDeleteIntuneConfirmed] = useState(false);
  const [isDeletingIntune, setIsDeletingIntune] = useState(false);

  // Deploy modal state
  const [deployingPackage, setDeployingPackage] = useState(null);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployProgress, setDeployProgress] = useState({ step: "", progress: 0, status: "pending" });
  
  // Test group modal state
  const [showTestGroupModal, setShowTestGroupModal] = useState(false);
  const [testMembers, setTestMembers] = useState([""]);
  const [silentMode, setSilentMode] = useState(false);
  
  // Detection Rule modal state
  const [showDetectionModal, setShowDetectionModal] = useState(false);
  const [detectionRule, setDetectionRule] = useState(null);
  const [loadingDetectionRule, setLoadingDetectionRule] = useState(false);
  const [detectionRuleType, setDetectionRuleType] = useState("file"); // "registry", "script", "msi", or "file"

  // Icon preview state (lives inside the Detection / pre-deploy modal so
  // the user can review and replace the icon Intune will receive)
  const [iconPreview, setIconPreview] = useState(null);     // { icon_base64, source, source_label, has_override }
  const [iconLoading, setIconLoading] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [iconResetting, setIconResetting] = useState(false);
  
  // Upload .intunewin modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadPackageId, setUploadPackageId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // MSI Properties modal state
  const [showMsiPropsModal, setShowMsiPropsModal] = useState(false);
  const [msiPropsPackage, setMsiPropsPackage] = useState(null);
  const [msiProperties, setMsiProperties] = useState(null);
  const [editedMsiProps, setEditedMsiProps] = useState({});
  const [loadingMsiProps, setLoadingMsiProps] = useState(false);
  const [savingMsiProps, setSavingMsiProps] = useState(false);
  const [msiExpertMode, setMsiExpertMode] = useState(false);
  // Phase 1 — MSI Transform (MST) mode: bake property overrides into
  // an .mst file rather than passing them via -AdditionalArgumentList.
  const [mstMode, setMstMode] = useState(false);
  const [mstPreview, setMstPreview] = useState(null);
  const [loadingMstPreview, setLoadingMstPreview] = useState(false);
  const [downloadingMst, setDownloadingMst] = useState(false);

  // Auto-update state
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [updatingPackage, setUpdatingPackage] = useState(null);
  const [showUpdateWarning, setShowUpdateWarning] = useState(false);

  // Supersede Apps modal state
  const [showSupersedeModal, setShowSupersedeModal] = useState(false);
  const [supersedeDeploymentType, setSupersedeDeploymentType] = useState("test");
  const [pendingTestMembers, setPendingTestMembers] = useState([]);
  const [supersedeAppsToExclude, setSupersedeAppsToExclude] = useState([]);

  // Description preview/edit modal (Intune description shown before deploy)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [descriptionSaving, setDescriptionSaving] = useState(false);

  // Compute stats from packages
  const stats = {
    total: pagination.total || packages.length,
    completed: packages.filter(p => p.status === "completed").length,
    processing: packages.filter(p => ["pending", "downloading", "processing", "packaging"].includes(p.status)).length,
    failed: packages.filter(p => p.status === "failed").length,
    updates_available: packages.filter(p => p.update_available).length,
  };

  // Server now handles search + sort + pagination. Keep `visiblePackages`
  // as an alias so the JSX can stay the same, and retain the empty-state
  // copy when a search returns 0 rows.
  const visiblePackages = packages;

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const SortIcon = ({ column }) => {
    if (sort.key !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sort.dir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Check for updates handler
  const handleCheckUpdates = async () => {
    setShowUpdateWarning(false);
    setCheckingUpdates(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/packages/check-updates`, {}, { headers });
      const autoTriggered = response.data.auto_updates_triggered || 0;
      if (autoTriggered > 0) {
        toast.success(`Checked ${response.data.checked} packages. ${response.data.updates_found} updates found. ${autoTriggered} auto-update(s) started!`);
      } else {
        toast.success(`Checked ${response.data.checked} packages. ${response.data.updates_found} updates found.`);
      }
      fetchPackages(); // Refresh to show update badges
    } catch (error) {
      console.error("Failed to check updates:", error);
      toast.error("Failed to check for updates");
    } finally {
      setCheckingUpdates(false);
    }
  };

  // Update package to latest version
  const handleUpdateToLatest = async (pkg) => {
    const usedBy = pkg.used_by || [];
    if (usedBy.length > 0) {
      const names = usedBy.map((a) => `  • ${a.app_name} (needs ${a.constraint})`).join("\n");
      const proceed = window.confirm(
        `${pkg.app_name} is a dependency of:\n${names}\n\nUpdating it can affect these apps. Check they support v${pkg.latest_available_version} before deploying. Continue?`
      );
      if (!proceed) return;
    }
    setUpdatingPackage(pkg.id);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/packages/${pkg.id}/update-to-latest`, {}, { headers });
      toast.success(`Update to v${response.data.version} initiated!`);
      fetchPackages();
    } catch (error) {
      console.error("Failed to update package:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate update");
    } finally {
      setUpdatingPackage(null);
    }
  };

  // Toggle auto-update for a package
  const handleToggleAutoUpdate = async (pkg, enabled) => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/packages/${pkg.id}/toggle-auto-update?enabled=${enabled}`, {}, { headers });
      toast.success(`Auto-update ${enabled ? 'enabled' : 'disabled'} for ${pkg.app_name}`);
      fetchPackages();
    } catch (error) {
      console.error("Failed to toggle auto-update:", error);
      toast.error("Failed to update setting");
    }
  };

  // Fetch packages
  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (searchQuery.trim()) params.append("search", searchQuery.trim());
      params.append("sort_by", sort.key);
      params.append("sort_dir", sort.dir);
      params.append("page", pagination.page.toString());
      params.append("page_size", pagination.pageSize.toString());

      const response = await axios.get(`${API}/packages?${params}`, { headers });
      setPackages(response.data.packages || []);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error("Failed to fetch packages:", error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery, sort.key, sort.dir, pagination.page, pagination.pageSize, getAuthHeaders]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const fetchHousekeeping = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API}/app-housekeeping/suggestions`, { headers });
      setHousekeeping({ suggestions: data.suggestions || [], latest_installs: data.latest_installs || {} });
    } catch (error) {
      // Suggestions are a convenience: the page works without them.
      console.error("Failed to load housekeeping suggestions:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchHousekeeping();
  }, [fetchHousekeeping]);

  // Tenant-wide auto-progression setting, shown as what "Default" means in
  // the per-app Auto-Progress column. null until loaded.
  const [autoProgressDefault, setAutoProgressDefault] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const { data } = await axios.get(`${API}/deployment-automation/settings`, { headers });
        setAutoProgressDefault(Boolean(data?.auto_progress_enabled));
      } catch (error) {
        // Only labels the "Default" option; the column works without it.
        console.error("Failed to load deployment automation settings:", error);
      }
    })();
  }, [getAuthHeaders]);

  // Per-app auto-progression: "default" follows the tenant setting, "on"
  // and "off" override it for this app and its auto-update versions.
  const autoProgressMode = (pkg) =>
    pkg.auto_progress_enabled === true ? "on" : pkg.auto_progress_enabled === false ? "off" : "default";

  const handleSetAutoProgress = async (pkg, mode) => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/packages/${pkg.id}/auto-progress`, { mode }, { headers });
      const label = mode === "default" ? "set to follow the tenant default" : mode === "on" ? "enabled" : "disabled";
      toast.success(`Auto-progression ${label} for ${pkg.app_name}`);
      fetchPackages();
    } catch (error) {
      console.error("Failed to set auto-progression:", error);
      toast.error(error.response?.data?.detail || "Failed to update setting");
    }
  };

  // Fetch package logs
  const fetchPackageLogs = async (packageId) => {
    setLoadingLogs(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/packages/${packageId}/logs`, { headers });
      setPackageLogs(response.data.logs || []);
    } catch (error) {
      toast.error("Failed to load logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  // View logs
  const handleViewLogs = async (pkg) => {
    setSelectedPackage(pkg);
    setShowLogsModal(true);
    await fetchPackageLogs(pkg.id);
  };

  // Download package
  const handleDownload = async (packageId) => {
    try {
      toast.info("Starting download...");
      
      // Use hidden iframe approach - works in sandboxed environments
      const downloadUrl = `${API}/packages/${packageId}/download`;
      
      // Create hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = downloadUrl;
      
      document.body.appendChild(iframe);
      
      // Remove iframe after download starts
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 5000);
      
      // Give user feedback
      toast.success("Download started - check your downloads folder");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download package");
    }
  };

  // Handle .intunewin upload
  const handleUploadIntunewin = async () => {
    if (!uploadFile || !uploadPackageId) {
      toast.error("Please select a file");
      return;
    }
    
    setUploading(true);
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      await axios.post(
        `${API}/packages/${uploadPackageId}/upload-intunewin`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success("Successfully uploaded .intunewin file!");
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadPackageId(null);
      fetchPackages();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload .intunewin file");
    } finally {
      setUploading(false);
    }
  };

  // Check for existing test group and start deploy flow
  const handleDeploy = async (pkg) => {
    setDeployingPackage(pkg);
    
    try {
      const headers = await getAuthHeaders();
      // Step 1: Check detection rule first - ALWAYS show modal for review/confirmation
      const detectionResp = await axios.get(`${API}/packages/${pkg.id}/detection-rule`, { headers });
      const ruleData = detectionResp.data;
      
      // Always show detection modal for user to review and confirm
      // This ensures correct detection rules before deployment
      setDetectionRule(ruleData.detection_rule);
      setDetectionRuleType(ruleData.detection_rule?.type || "file");
      // Kick off the icon preview fetch in parallel so the modal opens
      // ready to render the actual logo Intune is about to receive.
      loadIconPreview(pkg.id);
      setShowDetectionModal(true);
      return;
      
      // Note: The code below is kept for reference but bypassed
      // If detection rule requires review (low confidence or heuristic), show modal
      if (ruleData.requires_review) {
        setDetectionRule(ruleData.detection_rule);
        setDetectionRuleType(ruleData.detection_rule.type || "file");
        setShowDetectionModal(true);
        return;
      }
      
      // Step 2: Fetch existing test members (if any) for editing
      const response = await axios.get(`${API}/packages/${pkg.id}/test-members`, { headers });
      
      // Show test member modal with existing members (if any)
      if (response.data.test_members && response.data.test_members.length > 0) {
        // Pre-populate with existing members for editing
        setTestMembers(response.data.test_members);
      } else {
        // Start with one empty field
        setTestMembers([""]);
      }
      
      // Always show modal to allow editing
      setShowTestGroupModal(true);
    } catch (error) {
      toast.error("Failed to check deployment requirements");
    }
  };

  // ---- Icon preview & override (used inside the Detection / pre-deploy modal) ----
  const loadIconPreview = async (packageId) => {
    if (!packageId) return;
    setIconLoading(true);
    setIconPreview(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await axios.get(`${API}/packages/${packageId}/icon-preview`, { headers });
      setIconPreview(resp.data);
    } catch (error) {
      console.error("Failed to load icon preview:", error);
      setIconPreview({
        icon_base64: null,
        source: "error",
        source_label: "Could not load preview",
        has_override: false,
      });
    } finally {
      setIconLoading(false);
    }
  };

  const handleIconFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";  // allow re-selecting the same file
    if (!file || !deployingPackage) return;

    // Client-side guard rails — backend re-validates strictly.
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      toast.error("Only PNG or JPG files are accepted.");
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error(`Icon too large (${Math.round(file.size / 1024)} KB). Max is 1024 KB.`);
      return;
    }

    setIconUploading(true);
    try {
      const headers = await getAuthHeaders();
      const form = new FormData();
      form.append("file", file);
      const resp = await axios.post(
        `${API}/packages/${deployingPackage.id}/icon`,
        form,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      setIconPreview(resp.data);
      toast.success("Custom icon uploaded — it will be used on the next deploy.");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to upload icon";
      toast.error(msg);
    } finally {
      setIconUploading(false);
    }
  };

  const handleIconReset = async () => {
    if (!deployingPackage) return;
    setIconResetting(true);
    try {
      const headers = await getAuthHeaders();
      const resp = await axios.delete(
        `${API}/packages/${deployingPackage.id}/icon`,
        { headers },
      );
      setIconPreview(resp.data);
      toast.success("Reverted to auto-detected icon for this app.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset icon");
    } finally {
      setIconResetting(false);
    }
  };


  
  // Start test deployment with members
  const startTestDeployment = async (packageId, members, supersedeAppIds = []) => {
    try {
      // Get access token for Graph API
      const accessToken = await getAccessToken();
      
      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      
      const response = await axios.post(
        `${API}/packages/${packageId}/deploy`, 
        {
          test_members: members.filter(m => m.trim()),
          supersede_app_ids: supersedeAppIds,
          silent_mode: silentMode
        },
        {
          headers: headers
        }
      );
      
      if (response.data.status === "need_test_members") {
        // Still need test members
        setShowDeployModal(false);
        setTestMembers([""]);
        setShowTestGroupModal(true);
        return;
      }

      // The family's deployment pipeline starts with Pre-UAT: the app goes
      // Available to an existing Entra group first, with no testers yet.
      // Moving it on to UAT is a person's call on the Deployments page.
      if (response.data.pre_uat_group) {
        const g = response.data.pre_uat_group;
        toast.info(
          response.data.message ||
            `Deploying to ${g.name || "Pre-UAT"} (Entra group ${g.group_name}). Move it to UAT from the Deployments page when ready.`,
          { duration: 10000 }
        );
      }

      // Start polling
      pollDeployStatus(packageId);
    } catch (error) {
      setDeployProgress({ step: "failed", progress: 0, status: "failed", error: error.response?.data?.detail || "Deployment failed" });
      toast.error(error.response?.data?.detail || "Deployment failed");
    }
  };


  // Open detection rule configuration modal directly (without starting deployment)
  const handleConfigureDetection = async (pkg) => {
    setDeployingPackage(pkg);
    setLoadingDetectionRule(true);
    
    try {
      const headers = await getAuthHeaders();
      const detectionResp = await axios.get(`${API}/packages/${pkg.id}/detection-rule`, { headers });
      const ruleData = detectionResp.data;
      
      setDetectionRule(ruleData.detection_rule);
      setDetectionRuleType(ruleData.detection_rule?.type || "file");
      loadIconPreview(pkg.id);
      setShowDetectionModal(true);
    } catch (error) {
      toast.error("Failed to load detection rule");
    } finally {
      setLoadingDetectionRule(false);
    }
  };

  // Open MSI Properties modal
  const handleConfigureMsiProperties = async (pkg) => {
    if (pkg.installer_type?.toLowerCase() !== 'msi') {
      toast.error("MSI Properties are only available for MSI installers");
      return;
    }
    
    setMsiPropsPackage(pkg);
    setLoadingMsiProps(true);
    setShowMsiPropsModal(true);
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/packages/${pkg.id}/msi-properties`, { headers });
      setMsiProperties(response.data);
      setEditedMsiProps(response.data.configured_properties || {});
      // Pre-load MST preview in the background so the panel is populated
      // when the user toggles MST mode.
      loadMstPreview(pkg.id);
      // Reflect the persisted MST mode flag if the package has one.
      if (pkg.use_mst) setMstMode(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load MSI properties");
      setShowMsiPropsModal(false);
    } finally {
      setLoadingMsiProps(false);
    }
  };

  // Save MSI Properties
  const handleSaveMsiProperties = async () => {
    if (!msiPropsPackage) return;
    
    setSavingMsiProps(true);
    try {
      const headers = await getAuthHeaders();
      // Set MST mode flag FIRST so the subsequent property save
      // rewrites the deploy script under the correct mode.
      await axios.put(
        `${API}/packages/${msiPropsPackage.id}/mst/enabled`,
        { enabled: mstMode },
        { headers },
      );
      await axios.put(`${API}/packages/${msiPropsPackage.id}/msi-properties`, editedMsiProps, { headers });
      toast.success(mstMode ? "MSI properties saved — MST regenerated" : "MSI properties saved");
      // Refresh the MST preview to reflect newly saved values
      if (mstMode) {
        loadMstPreview(msiPropsPackage.id);
      }
      setShowMsiPropsModal(false);
      fetchPackages(); // Refresh to show updated data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save MSI properties");
    } finally {
      setSavingMsiProps(false);
    }
  };

  // Toggle MSI property
  const toggleMsiProperty = (propName, defaultValue = "1") => {
    setEditedMsiProps(prev => {
      const newProps = { ...prev };
      if (propName in newProps) {
        delete newProps[propName];
      } else {
        newProps[propName] = defaultValue;
      }
      return newProps;
    });
  };

  // Update MSI property value
  const updateMsiPropertyValue = (propName, value) => {
    setEditedMsiProps(prev => ({
      ...prev,
      [propName]: value
    }));
  };

  // -------------------------------------------------------------------
  // Phase 1 — MSI Transform (MST) helpers
  // -------------------------------------------------------------------
  const loadMstPreview = async (packageId) => {
    if (!packageId) return;
    setLoadingMstPreview(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API}/packages/${packageId}/mst/preview`, { headers });
      setMstPreview(data);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to load MST preview";
      toast.error(msg);
      setMstPreview(null);
    } finally {
      setLoadingMstPreview(false);
    }
  };

  const downloadMst = async (packageId, appName) => {
    if (!packageId) return;
    setDownloadingMst(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/packages/${packageId}/mst/download`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(appName || packageId).replace(/[^A-Za-z0-9_.-]/g, "_")}.mst`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`MST downloaded (${response.data.size} bytes)`);
    } catch (error) {
      const msg = error.response?.data?.detail || "MST generation failed";
      toast.error(msg);
    } finally {
      setDownloadingMst(false);
    }
  };

  
  // Handle detection rule confirmation and save
  const handleDetectionRuleConfirm = async () => {
    try {
      setLoadingDetectionRule(true);
      
      // Validate inputs based on detection type
      if (detectionRuleType === "msi") {
        if (!detectionRule?.productCode || !detectionRule.productCode.trim()) {
          toast.error("Product Code is required for MSI detection");
          setLoadingDetectionRule(false);
          return;
        }
      } else if (detectionRuleType === "registry") {
        if (!detectionRule?.displayName || !detectionRule.displayName.trim()) {
          toast.error("Display Name is required for registry detection");
          setLoadingDetectionRule(false);
          return;
        }
      } else if (detectionRuleType === "script") {
        if (!detectionRule?.scriptContent || !detectionRule.scriptContent.trim()) {
          toast.error("Script content is required for script detection");
          setLoadingDetectionRule(false);
          return;
        }
      } else if (detectionRuleType === "file") {
        if (!detectionRule?.path || !detectionRule.path.trim()) {
          toast.error("Path is required for file detection");
          setLoadingDetectionRule(false);
          return;
        }
        if (!detectionRule?.fileName || !detectionRule.fileName.trim()) {
          toast.error("File Name is required for file detection");
          setLoadingDetectionRule(false);
          return;
        }
      }
      
      // Validate version/size fields if needed
      if (detectionRuleType === "file" && detectionRule?.detectionType !== "exists") {
        if (!detectionRule?.operator) {
          toast.error("Operator is required for version/size detection");
          setLoadingDetectionRule(false);
          return;
        }
        if (!detectionRule?.comparisonValue || !detectionRule.comparisonValue.trim()) {
          toast.error(detectionRule?.detectionType === 'version' ? "Version value is required" : "Size value is required");
          setLoadingDetectionRule(false);
          return;
        }
      }
      
      const headers = await getAuthHeaders();
      // Save detection rule
      await axios.put(
        `${API}/packages/${deployingPackage.id}/detection-rule`,
        {
          type: detectionRuleType,
          // MSI fields
          productCode: detectionRule?.productCode || null,
          productVersion: detectionRule?.productVersion || null,
          // File fields
          path: detectionRule?.path || null,
          fileName: detectionRule?.fileName || null,
          detectionType: detectionRule?.detectionType || "exists",
          operator: detectionRule?.operator || null,
          comparisonValue: detectionRule?.comparisonValue || null,
          check32BitOn64System: detectionRule?.check32BitOn64System || false,
          // Registry fields (Tier 1)
          displayName: detectionRule?.displayName || null,
          displayVersion: detectionRule?.displayVersion || null,
          publisher: detectionRule?.publisher || null,
          versionComparison: detectionRule?.versionComparison || "greaterThanOrEqual",
          // Script fields (Tier 2)
          scriptContent: detectionRule?.scriptContent || null,
          // Metadata
          tier: detectionRule?.tier || null,
          confidence: detectionRule?.confidence || "high"
        },
        { headers }
      );
      
      toast.success("Detection rule saved");
      setShowDetectionModal(false);
      setLoadingDetectionRule(false);

      // Show the description preview modal so the operator can review/edit
      // the description Intune is about to receive before kicking off the
      // supersede + test deploy flow.
      await openDescriptionModal();

    } catch (error) {
      setLoadingDetectionRule(false);
      toast.error(error.response?.data?.detail || "Failed to save detection rule");
    }
  };

  // ---- Description preview/edit (new wizard step before supersede) ----
  const openDescriptionModal = async () => {
    if (!deployingPackage) return;
    setDescriptionLoading(true);
    setShowDescriptionModal(true);
    try {
      const headers = await getAuthHeaders();
      const resp = await axios.get(
        `${API}/packages/${deployingPackage.id}/description`,
        { headers }
      );
      setDescriptionDraft(resp.data?.description || "");
    } catch (error) {
      console.error("Failed to load description preview:", error);
      setDescriptionDraft(deployingPackage?.description || "");
      toast.error("Could not load auto-generated description");
    } finally {
      setDescriptionLoading(false);
    }
  };

  const handleDescriptionConfirm = async () => {
    if (!deployingPackage) return;
    const trimmed = (descriptionDraft || "").trim();
    if (trimmed.length > 4000) {
      toast.error("Description must be 4000 characters or fewer");
      return;
    }
    setDescriptionSaving(true);
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${API}/packages/${deployingPackage.id}/description`,
        { description: trimmed },
        { headers }
      );
      setShowDescriptionModal(false);
      // Continue the wizard to the supersede step
      setSupersedeDeploymentType("test");
      setShowSupersedeModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save description");
    } finally {
      setDescriptionSaving(false);
    }
  };

  // Handle supersede modal confirmation - proceed to test group selection
  const handleSupersedeConfirm = async (selectedAppIds = []) => {
    setShowSupersedeModal(false);
    
    // Store selected apps for supersedence during deployment
    setSupersedeAppsToExclude(selectedAppIds || []);
    if (selectedAppIds?.length > 0) {
      console.log(`Supersede: Will set supersedence on ${selectedAppIds.length} app(s) after deployment`);
    }
    
    // Check for existing testers in test_groups (synced from Discover governance)
    try {
      const headers = await getAuthHeaders();
      const testMembersResponse = await axios.get(`${API}/packages/${deployingPackage.id}/test-members`, { headers });
      const existingTesters = testMembersResponse.data?.test_members || [];
      
      if (existingTesters.length > 0) {
        // Pre-populate the test group modal with governance testers
        // Still show the modal so user can configure silent mode
        setTestMembers(existingTesters);
      } else {
        setTestMembers([""]);
      }
    } catch (testError) {
      console.log("Could not fetch test members:", testError);
      setTestMembers([""]);
    }
    
    // Always show the test group modal for silent mode toggle + member review
    setShowTestGroupModal(true);
  };
  
  // Submit test group members and deploy
  const handleSubmitTestGroup = async () => {
    const validMembers = testMembers.filter(m => m.trim());
    const uninstallOnly = !!deployingPackage?.uninstall_only;

    if (validMembers.length === 0) {
      toast.error(
        uninstallOnly
          ? "Please add at least one device to uninstall from"
          : "Please add at least one test user/device"
      );
      return;
    }

    // Uninstall-only packages deploy with Intune's uninstall intent, which
    // has to target machines: aimed at a user group it would remove the app
    // from every device that person signs into. The backend rejects user
    // accounts too, but the PUT below is deliberately non-fatal, so catch it
    // here or the deployment would carry on with a group we cannot populate.
    if (uninstallOnly) {
      const userShaped = validMembers.filter(m => m.includes("@"));
      if (userShaped.length > 0) {
        toast.error(
          `Uninstall-only packages target devices, not users. Remove ${userShaped.join(", ")} and enter device names instead.`
        );
        return;
      }
    }
    
    // Save testers to test_groups for future deployments
    try {
      const headers = await getAuthHeaders();
      // The deploy call below emails testers; don't send a second invite here.
      await axios.put(`${API}/packages/${deployingPackage.id}/test-members`, {
        members: validMembers,
        notify_new_members: false
      }, { headers });
      toast.info("Testers saved for future deployments");
    } catch (saveError) {
      console.log("Could not save testers:", saveError);
    }
    
    setShowTestGroupModal(false);
    setShowDeployModal(true);
    setDeployProgress({ step: "wrapping", progress: 10, status: "in_progress" });
    startTestDeployment(deployingPackage.id, validMembers, supersedeAppsToExclude);
  };
  
  // Add test member input
  const addTestMember = () => {
    if (testMembers.length < 5) {
      setTestMembers([...testMembers, ""]);
    }
  };
  
  // Update test member
  const updateTestMember = (index, value) => {
    const updated = [...testMembers];
    updated[index] = value;
    setTestMembers(updated);
  };
  
  // Remove test member
  const removeTestMember = (index) => {
    if (testMembers.length > 1) {
      setTestMembers(testMembers.filter((_, i) => i !== index));
    }
  };
  
  // Poll deployment status
  const pollDeployStatus = async (packageId) => {
    const poll = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API}/packages/${packageId}/status`, { headers });
        const status = response.data;
        
        if (status.status === "packaging") {
          setDeployProgress({ step: "wrapping", progress: 30, status: "in_progress" });
          setTimeout(poll, 1500);
        } else if (status.status === "uploading") {
          setDeployProgress({ step: "deploying", progress: 65, status: "in_progress" });
          setTimeout(poll, 1500);
        } else if (status.status === "completed") {
          setDeployProgress({ step: "complete", progress: 100, status: "completed" });
          toast.success("Test deployment complete!");
          fetchPackages();
        } else if (status.status === "failed") {
          setDeployProgress({ step: "failed", progress: 0, status: "failed", error: status.error_message });
          toast.error(`Deployment failed: ${status.error_message}`);
        } else {
          setTimeout(poll, 1500);
        }
      } catch (error) {
        console.error("Failed to get deploy status:", error);
      }
    };
    poll();
  };

  // Delete package
  const handleDelete = async () => {
    if (!deletePackageId) return;
    setIsDeleting(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (deleteIntuneApp) params.append("delete_intune_app", "true");
      if (deleteIntuneGroups) params.append("delete_intune_groups", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await axios.delete(
        `${API}/packages/${deletePackageId}${qs}`,
        { headers }
      );

      // Build a rich toast summarising Intune cleanup
      const cleanup = data?.intune_cleanup || {};
      const parts = ["Package deleted"];
      if (cleanup.requested_app_delete) {
        parts.push(
          cleanup.intune_app_deleted
            ? "Intune app removed"
            : "Intune app NOT removed"
        );
      }
      if (cleanup.requested_groups_delete) {
        const ok = (cleanup.groups_deleted || []).length;
        const failed = (cleanup.groups_failed || []).length;
        parts.push(`${ok} group(s) removed${failed ? `, ${failed} failed` : ""}`);
      }
      const hasErrors =
        (cleanup.errors && cleanup.errors.length) ||
        (cleanup.groups_failed && cleanup.groups_failed.length);
      if (hasErrors) {
        toast.warning(parts.join(" • "), {
          description: [
            ...(cleanup.errors || []),
            ...(cleanup.groups_failed || []).map(
              (g) => `Group ${g.group_id}: ${g.detail || g.error || g.status}`
            ),
          ]
            .slice(0, 3)
            .join("\n"),
        });
      } else {
        toast.success(parts.join(" • "));
      }

      setDeletePackageId(null);
      setDeleteIntuneApp(false);
      setDeleteIntuneGroups(false);
      fetchPackages();
    } catch (error) {
      console.error("Failed to delete package:", error);
      toast.error(
        error?.response?.data?.detail || "Failed to delete package"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Retire from Intune: preview first (what will change, and why it may be
  // blocked), then retire with the preview's plan_hash so the server
  // refuses if Intune changed in between.
  const openRetire = async (pkg) => {
    setRetirePackage(pkg);
    setRetirePreview(null);
    setRetirePreviewError(null);
    try {
      const headers = await getAuthHeaders();
      // Always this version and every older one still active: retiring v2
      // alone would remove "v2 supersedes v1" while v1 keeps its
      // assignments. On the current live version this means "everything
      // older". The response's package_ids are what gets retired.
      const { data } = await axios.post(
        `${API}/app-housekeeping/preview`,
        { package_ids: [pkg.id], include_older: true },
        { headers }
      );
      setRetirePreview(data);
    } catch (error) {
      const status = error?.response?.status;
      setRetirePreviewError(
        error?.response?.data?.detail ||
          `Couldn't load the preview${status ? ` (HTTP ${status})` : ` (${error?.message || "network error"})`}`
      );
    }
  };

  const closeRetire = () => {
    setRetirePackage(null);
    setRetirePreview(null);
    setRetirePreviewError(null);
  };

  const handleRetire = async () => {
    if (!retirePackage || !retirePreview || !(retirePreview.package_ids || []).length) return;
    setIsRetiring(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.post(
        `${API}/app-housekeeping/retire`,
        { package_ids: retirePreview.package_ids, plan_hash: retirePreview.plan_hash },
        { headers }
      );
      const results = data.results || [];
      const failed = results.find((r) => !r.ok);
      const done = results.filter((r) => r.ok).map((r) => r.version);
      if (!failed) {
        toast.success(`${retirePackage.app_name} ${done.join(", ")} retired from Intune`);
      } else {
        toast.error(
          `Retired ${done.length ? done.join(", ") : "nothing"}; ${failed.version} stopped at ${failed.failed_step || "an unknown step"}`,
          { description: failed.error }
        );
      }
      closeRetire();
      fetchPackages();
      fetchHousekeeping();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to retire");
      // A 409 means Intune changed since the preview: show the new one.
      if (error?.response?.status === 409) openRetire(retirePackage);
    } finally {
      setIsRetiring(false);
    }
  };

  // Delete a retired version from Intune: preview (apps, groups deleted and
  // kept, blockers), then delete with the preview's plan_hash.
  const openDeleteIntune = async (pkg) => {
    setDeleteIntunePkg(pkg);
    setDeleteIntunePreview(null);
    setDeleteIntuneError(null);
    setDeleteIntuneConfirmed(false);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.post(
        `${API}/app-housekeeping/delete-preview`,
        { package_ids: [pkg.id] },
        { headers }
      );
      setDeleteIntunePreview(data);
    } catch (error) {
      const status = error?.response?.status;
      setDeleteIntuneError(
        error?.response?.data?.detail ||
          `Couldn't load the preview${status ? ` (HTTP ${status})` : ` (${error?.message || "network error"})`}`
      );
    }
  };

  const closeDeleteIntune = () => {
    setDeleteIntunePkg(null);
    setDeleteIntunePreview(null);
    setDeleteIntuneError(null);
    setDeleteIntuneConfirmed(false);
  };

  const handleDeleteIntune = async () => {
    if (!deleteIntunePkg || !deleteIntunePreview) return;
    setIsDeletingIntune(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.post(
        `${API}/app-housekeeping/delete`,
        { package_ids: deleteIntunePreview.package_ids, plan_hash: deleteIntunePreview.plan_hash },
        { headers }
      );
      const failed = (data.results || []).find((r) => !r.ok);
      if (!failed) {
        toast.success(`${deleteIntunePkg.app_name} ${deleteIntunePkg.version} deleted from Intune`);
      } else {
        toast.error(`Delete stopped at ${failed.failed_step || "an unknown step"}`, { description: failed.error });
      }
      closeDeleteIntune();
      fetchPackages();
      fetchHousekeeping();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete from Intune");
      if (error?.response?.status === 409) openDeleteIntune(deleteIntunePkg);
    } finally {
      setIsDeletingIntune(false);
    }
  };

  const suggestionByPackage = Object.fromEntries(
    (housekeeping.suggestions || []).map((s) => [s.package_id, s])
  );
  const toRetire = (housekeeping.suggestions || []).filter((s) => s.action === "retire");
  const toDelete = (housekeeping.suggestions || []).filter((s) => s.action === "delete");

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "outline", icon: Clock, className: "text-yellow-300 border-2 border-yellow-500 bg-yellow-500/20 font-semibold" },
      downloading: { variant: "outline", icon: Loader2, className: "text-blue-300 border-2 border-blue-500 bg-blue-500/20 font-semibold" },
      scanning: { variant: "outline", icon: ShieldCheck, className: "text-cyan-300 border-2 border-cyan-500 bg-cyan-500/20 font-semibold" },
      processing: { variant: "outline", icon: Loader2, className: "text-blue-300 border-2 border-blue-500 bg-blue-500/20 font-semibold" },
      packaging: { variant: "outline", icon: Loader2, className: "text-blue-300 border-2 border-blue-500 bg-blue-500/20 font-semibold" },
      uploading: { variant: "outline", icon: Upload, className: "text-blue-300 border-2 border-blue-500 bg-blue-500/20 font-semibold" },
      completed: { variant: "outline", icon: Check, className: "text-green-300 border-2 border-green-500 bg-green-500/20 font-semibold" },
      failed: { variant: "outline", icon: X, className: "text-red-300 border-2 border-red-500 bg-red-500/20 font-semibold" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    // Bug fix 2026-07-29: `status` can be undefined on legacy packages
    // docs (or firmware shadow docs that leaked through the exclusion
    // filter). Guard the .includes() call — otherwise the whole
    // PackagesPage render crashes and users see a blank screen.
    const statusStr = status || 'pending';

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className={`h-3 w-3 mr-1 ${statusStr.includes("ing") && statusStr !== "packaging" ? "animate-spin" : ""}`} />
        {statusStr}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div className="min-h-screen bg-transparent">
      {/* Main Content */}
      <main className="p-6 w-full" data-testid="packages-main">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Packages</h1>
            <p className="text-white/90 mt-1">View and manage your created packages</p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
                }}
                placeholder="Search by name, publisher, ID…"
                className="pl-8 w-[260px] bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white placeholder:text-white/60"
                data-testid="packages-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowUpdateWarning(true)}
              disabled={checkingUpdates}
              className="border-[rgba(229,239,250,0.1)] hover:bg-white/[0.04] text-orange-400 hover:text-orange-300"
              data-testid="check-updates-btn"
            >
              {checkingUpdates ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Check for Updates
            </Button>
            
            <Select value={statusFilter || "all"} onValueChange={(v) => {
              setStatusFilter(v === "all" ? "" : v);
              setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
            }}>
              <SelectTrigger className="w-[160px] border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="downloading">Downloading</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="packaging">Packaging</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="icon"
              onClick={fetchPackages}
              className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC]"
              data-testid="refresh-packages"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Stats Cards - Glass Effect */}
        <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-total">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Total</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/20">
                <Package className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.total}</div>
            <div className="text-sm text-white/90">Packages</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-completed">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Completed</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/20">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.completed}</div>
            <div className="text-sm text-white/90">Ready to Deploy</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-processing">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Processing</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-400/20">
                <Loader2 className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.processing}</div>
            <div className="text-sm text-white/90">In Progress</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-failed">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Failed</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-400/20">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.failed}</div>
            <div className="text-sm text-white/90">Errors</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-updates">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Updates</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-400/20">
                <ArrowUpCircle className="w-5 h-5 text-orange-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.updates_available}</div>
            <div className="text-sm text-white/90">Available</div>
          </div>
        </div>

        {/* Housekeeping: old versions ready to retire or delete from Intune */}
        {(toRetire.length > 0 || toDelete.length > 0) && (
          <div
            className="mb-6 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl"
            data-testid="housekeeping-panel"
          >
            <button
              type="button"
              onClick={() => setHousekeepingOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
              aria-expanded={housekeepingOpen}
              data-testid="housekeeping-toggle"
            >
              <span className="flex items-center gap-3">
                <Archive className="h-5 w-5 text-white" />
                <span className="font-semibold text-white">Housekeeping</span>
                <span className="text-sm text-white/80">
                  {toRetire.length > 0 && `${toRetire.length} version${toRetire.length === 1 ? "" : "s"} ready to retire`}
                  {toRetire.length > 0 && toDelete.length > 0 && " · "}
                  {toDelete.length > 0 && `${toDelete.length} ready to delete from Intune`}
                </span>
              </span>
              {housekeepingOpen ? <ChevronDown className="h-4 w-4 text-white" /> : <ChevronRight className="h-4 w-4 text-white" />}
            </button>
            {housekeepingOpen && (
              <ul className="border-t border-white/10 divide-y divide-white/10">
                {[...toRetire, ...toDelete].map((s) => (
                  <li key={`${s.action}-${s.package_id}`} className="flex items-start justify-between gap-4 px-5 py-3" data-testid={`housekeeping-item-${s.package_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white">
                        <span className="font-medium">{s.app_name}</span>{" "}
                        <span className="font-mono">{s.version}</span>
                        <span className="text-white/60"> · current {s.current_version}</span>
                      </p>
                      <ul className="mt-1 text-xs text-white/75 list-disc pl-4">
                        {s.reasons.map((r) => <li key={r}>{r}</li>)}
                      </ul>
                    </div>
                    {canManageApps && (
                      s.action === "retire" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openRetire({ id: s.package_id, app_name: s.app_name, version: s.version })}
                          className="shrink-0"
                          data-testid={`housekeeping-retire-${s.package_id}`}
                        >
                          <Archive className="h-4 w-4 mr-1" /> Retire
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteIntune({ id: s.package_id, app_name: s.app_name, version: s.version })}
                          className="shrink-0 text-red-300 border-red-500/60"
                          data-testid={`housekeeping-delete-${s.package_id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" /> Delete from Intune
                        </Button>
                      )
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Packages Table */}
        <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          ) : packages.length === 0 ? (
            searchQuery || statusFilter ? (
              <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="packages-search-empty">
                <Search className="h-10 w-10 mb-3 text-white/40" />
                <p className="text-white/90">
                  No packages match{searchQuery ? ` "${searchQuery}"` : ""}
                  {statusFilter ? ` with status "${statusFilter}"` : ""}
                </p>
                <Button
                  variant="outline"
                  onClick={() => { setSearchQuery(""); setStatusFilter(""); }}
                  className="mt-3"
                  data-testid="packages-search-clear"
                >
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 mb-4 text-[#E5EFFA]/30" />
                <p className="text-white/90">No packages found</p>
                <p className="text-sm text-white/70 mt-2">
                  Discover applications to package from the Discover → Rationalisation tab
                </p>
                <Link to="/discover?view=rationalisation">
                  <Button className="mt-4 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
                    Go to Rationalisation
                  </Button>
                </Link>
              </div>
            )
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[rgba(229,239,250,0.1)] hover:bg-white/[0.04]">
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("app_name")}
                    data-testid="sort-app-name"
                  >
                    <span className="inline-flex items-center">Application<SortIcon column="app_name" /></span>
                  </TableHead>
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("version")}
                    data-testid="sort-version"
                  >
                    <span className="inline-flex items-center">Version<SortIcon column="version" /></span>
                  </TableHead>
                  <TableHead className="text-white">Format</TableHead>
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("status")}
                    data-testid="sort-status"
                  >
                    <span className="inline-flex items-center">Status<SortIcon column="status" /></span>
                  </TableHead>
                  <TableHead className="text-white">Auto-Update</TableHead>
                  <TableHead
                    className="text-white"
                    title="Whether this app's phased rollout advances on its own once the success thresholds in Settings are met. Default follows the tenant setting."
                  >
                    Auto-Progress
                  </TableHead>
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("created_at")}
                    data-testid="sort-created-at"
                  >
                    <span className="inline-flex items-center">Created<SortIcon column="created_at" /></span>
                  </TableHead>
                  <TableHead className="text-right text-white">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePackages.map((parentPkg) => {
                  const dependencyPackages = parentPkg.dependency_packages || [];
                  const dependenciesExpanded = !!expandedDependencies[parentPkg.id];
                  const renderPackageRow = (pkg, isChild) => (
                  <TableRow key={pkg.id} data-testid={`package-row-${pkg.id}`} className={`border-[rgba(229,239,250,0.1)] hover:bg-white/[0.04] ${pkg.update_available ? "bg-orange-500/10" : ""} ${isChild ? "bg-blue-500/[0.04]" : ""}`}>
                    <TableCell>
                      <div className={`flex items-start gap-2 ${isChild ? "pl-6" : ""}`}>
                        {isChild ? (
                          <CornerDownRight className="h-4 w-4 mt-0.5 text-white/50 shrink-0" />
                        ) : dependencyPackages.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setExpandedDependencies((prev) => ({ ...prev, [pkg.id]: !prev[pkg.id] }))}
                            className="mt-0.5 text-white/80 hover:text-white shrink-0"
                            title={dependenciesExpanded ? "Hide dependencies" : "Show dependencies"}
                            aria-expanded={dependenciesExpanded}
                            data-testid={`toggle-dependencies-${pkg.id}`}
                          >
                            {dependenciesExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        ) : null}
                      <div>
                        <Link
                          to={`/apps/${encodeURIComponent(pkg.winget_app_id)}`}
                          className="font-medium text-white hover:text-white/90 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {pkg.app_name}
                        </Link>
                        <p className="text-xs text-white/80 font-mono">{pkg.winget_app_id}</p>
                        {pkg.is_dependency && (
                          <Badge
                            variant="outline"
                            className="mt-1 text-blue-300 border-blue-500/60 bg-blue-500/15 text-[10px] px-1.5 py-0"
                            title={`Packaged as a dependency of ${pkg.parent_app_name || "another application"}. It is uploaded to Intune and linked automatically when the parent is deployed.`}
                            data-testid={`dependency-badge-${pkg.id}`}
                          >
                            <Link2 className="h-3 w-3 mr-0.5" />
                            Dependency{!isChild && pkg.parent_app_name ? ` of ${pkg.parent_app_name}` : ""}
                          </Badge>
                        )}
                        {(pkg.used_by || []).length > 0 && (
                          <Link
                            to={`/discover/governance/${encodeURIComponent(pkg.dependency_family || pkg.app_name)}?from=packages`}
                            className="inline-flex items-center mt-1 ml-1 px-1.5 py-0 rounded border border-amber-500/60 bg-amber-500/15 text-amber-200 text-[10px] hover:bg-amber-500/25"
                            title={`Required by:\n${pkg.used_by.map((a) => `  • ${a.app_name} (needs ${a.constraint})`).join("\n")}\nUpdating this app can affect them. Click to see its dependencies in App Governance.`}
                            data-testid={`used-by-badge-${pkg.id}`}
                          >
                            Used by {pkg.used_by.length}
                          </Link>
                        )}
                        {!isChild && dependencyPackages.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setExpandedDependencies((prev) => ({ ...prev, [pkg.id]: !prev[pkg.id] }))}
                            className="block mt-1 text-[11px] text-blue-300 hover:underline"
                            data-testid={`dependency-count-${pkg.id}`}
                          >
                            {dependencyPackages.length} {dependencyPackages.length === 1 ? "dependency" : "dependencies"}
                          </button>
                        )}
                      </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="secondary" className="font-mono w-fit bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white">{pkg.version}</Badge>
                        {pkg.update_available && pkg.latest_available_version && (
                          <Badge variant="outline" className="text-orange-300 border-2 border-orange-500 bg-orange-500/20 font-semibold w-fit text-xs">
                            <ArrowUpCircle className="h-3 w-3 mr-1" />
                            v{pkg.latest_available_version} available
                          </Badge>
                        )}
                        {pkg.lifecycle === "retired" && (
                          <Badge
                            variant="outline"
                            className="text-slate-300 border-slate-500 bg-slate-500/20 w-fit text-xs"
                            title={`Retired from Intune${pkg.retired_at ? ` on ${formatDate(pkg.retired_at)}` : ""}${pkg.retired_by ? ` by ${pkg.retired_by}` : ""}. The app is still in Intune, unassigned and with no supersedence.`}
                            data-testid={`retired-badge-${pkg.id}`}
                          >
                            <Archive className="h-3 w-3 mr-1" />
                            Retired
                          </Badge>
                        )}
                        {pkg.supersedence_warning && (
                          <Badge
                            variant="outline"
                            className="text-amber-300 border-amber-500 bg-amber-500/20 w-fit text-xs"
                            title={pkg.supersedence_warning}
                            data-testid={`supersedence-warning-badge-${pkg.id}`}
                          >
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Supersedence not set
                          </Badge>
                        )}
                        {suggestionByPackage[pkg.id] && (
                          <Badge
                            variant="outline"
                            className={`w-fit text-xs ${suggestionByPackage[pkg.id].action === "delete"
                              ? "text-red-300 border-red-500/60 bg-red-500/15"
                              : "text-sky-300 border-sky-500/60 bg-sky-500/15"}`}
                            title={suggestionByPackage[pkg.id].reasons.join("\n")}
                            data-testid={`housekeeping-badge-${pkg.id}`}
                          >
                            {suggestionByPackage[pkg.id].action === "delete" ? "Ready to delete from Intune" : "Ready to retire"}
                          </Badge>
                        )}
                        {housekeeping.latest_installs?.[pkg.id] && (
                          <span
                            className="text-[11px] text-white/70"
                            title={`Intune install count on ${housekeeping.latest_installs[pkg.id].date}`}
                            data-testid={`latest-installs-${pkg.id}`}
                          >
                            {housekeeping.latest_installs[pkg.id].installed ?? "?"} installed
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm uppercase text-white">{pkg.package_format}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(pkg.status)}
                        {pkg.security_scan?.status === "passed" && (
                          <Badge
                            variant="outline"
                            className="text-green-300 border-green-500/60 bg-green-500/10 text-[10px] px-1.5 py-0"
                            title={
                              (pkg.security_scan.sha256_verified
                                ? `SHA-256 verified against winget manifest`
                                : `SHA-256 recorded (no winget hash to verify against)`) +
                              ` · ClamAV scan clean` +
                              (pkg.security_scan.sha256
                                ? `\nSHA-256: ${pkg.security_scan.sha256}`
                                : "")
                            }
                            data-testid={`security-scan-pass-${pkg.id}`}
                          >
                            <ShieldCheck className="h-3 w-3 mr-0.5" />
                            Scanned
                          </Badge>
                        )}
                        {pkg.security_scan?.status === "failed" && (
                          <Badge
                            variant="outline"
                            className="text-red-300 border-red-500/60 bg-red-500/10 text-[10px] px-1.5 py-0"
                            title={pkg.security_scan.reason || "Security scan failed"}
                            data-testid={`security-scan-fail-${pkg.id}`}
                          >
                            <ShieldAlert className="h-3 w-3 mr-0.5" />
                            Scan failed
                          </Badge>
                        )}
                        {pkg.freshservice_ticket_id && (
                          <Badge
                            variant="outline"
                            className={`text-emerald-300 border-emerald-500/60 bg-emerald-500/10 text-[10px] px-1.5 py-0 ${pkg.freshservice_domain_used ? "cursor-pointer hover:bg-emerald-500/20" : ""}`}
                            title={`Freshservice ticket #${pkg.freshservice_ticket_id}${pkg.freshservice_last_result?.stage ? ` · last stage: ${pkg.freshservice_last_result.stage}` : ""}${pkg.freshservice_domain_used ? " · click to open" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (pkg.freshservice_domain_used) {
                                window.open(`https://${pkg.freshservice_domain_used}/a/tickets/${pkg.freshservice_ticket_id}`, "_blank");
                              }
                            }}
                            data-testid={`fs-ticket-${pkg.id}`}
                          >
                            <Ticket className="h-3 w-3 mr-0.5" />
                            FS #{pkg.freshservice_ticket_id}
                          </Badge>
                        )}
                        {!pkg.freshservice_ticket_id && pkg.freshservice_last_result && !pkg.freshservice_last_result.ok && !pkg.freshservice_last_result.skipped && (
                          <Badge
                            variant="outline"
                            className="text-amber-300 border-amber-500/60 bg-amber-500/10 text-[10px] px-1.5 py-0"
                            title={`Freshservice ticket not created (stage: ${pkg.freshservice_last_result.stage || "?"}): ${pkg.freshservice_last_result.error || "unknown error"}`}
                            data-testid={`fs-ticket-issue-${pkg.id}`}
                          >
                            <Ticket className="h-3 w-3 mr-0.5" />
                            FS failed
                          </Badge>
                        )}
                        {/* Phase 1 — Option E. Surface the configured
                            MSI shortcut removals so admins can see at
                            a glance which packages have a cleanup
                            customisation without opening the build
                            wizard or PSADT script. Hover lists the
                            actual .lnk names. */}
                        {Array.isArray(pkg.msi_shortcut_removals) && pkg.msi_shortcut_removals.length > 0 && (
                          <Badge
                            variant="outline"
                            className="text-pink-200 border-pink-500/60 bg-pink-500/15 text-[10px] px-1.5 py-0"
                            title={
                              "Removing on install:\n" +
                              pkg.msi_shortcut_removals
                                .map((entry) => {
                                  // Entries are either rich {name, is_desktop, ...}
                                  // dicts (post-refactor) or bare name strings
                                  // (legacy). Render the same line either way and
                                  // append a short location hint when we have it.
                                  if (entry && typeof entry === "object") {
                                    const loc = entry.is_desktop
                                      ? entry.desktop_scope === "user"
                                        ? "user Desktop"
                                        : "all-users Desktop"
                                      : entry.start_menu_subpath
                                      ? `Start Menu\\${entry.start_menu_subpath}`
                                      : "Start Menu";
                                    return `  • ${entry.name} (${loc})`;
                                  }
                                  return "  • " + String(entry);
                                })
                                .join("\n")
                            }
                            data-testid={`msi-shortcuts-removed-badge-${pkg.id}`}
                          >
                            🪦 {pkg.msi_shortcut_removals.length} shortcut
                            {pkg.msi_shortcut_removals.length === 1 ? "" : "s"} removed
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {pkg.status === "completed" && !pkg.winget_app_id?.startsWith('Custom.') ? (
                        <button
                          onClick={() => handleToggleAutoUpdate(pkg, !pkg.auto_update_enabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            pkg.auto_update_enabled ? 'bg-green-500' : 'bg-white/20'
                          }`}
                          title={pkg.auto_update_enabled ? "Auto-update enabled" : "Auto-update disabled"}
                          data-testid={`auto-update-toggle-${pkg.id}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              pkg.auto_update_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      ) : (
                        <span className="text-xs text-white/70">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {pkg.status === "completed" ? (
                        canDeployPackages ? (
                          <select
                            value={autoProgressMode(pkg)}
                            onChange={(e) => handleSetAutoProgress(pkg, e.target.value)}
                            className={`rounded-md border border-white/20 bg-white/10 px-2 py-1 text-xs ${
                              autoProgressMode(pkg) === "on" ? "text-green-400"
                                : autoProgressMode(pkg) === "off" ? "text-amber-300"
                                : "text-white"
                            }`}
                            title="Default follows the tenant setting in Settings → Deployment Automation"
                            data-testid={`auto-progress-select-${pkg.id}`}
                          >
                            <option value="default" className="text-black">
                              Default{autoProgressDefault === null ? "" : autoProgressDefault ? " (On)" : " (Off)"}
                            </option>
                            <option value="on" className="text-black">On</option>
                            <option value="off" className="text-black">Off</option>
                          </select>
                        ) : (
                          <span className="text-xs text-white/70">
                            {autoProgressMode(pkg) === "on" ? "On" : autoProgressMode(pkg) === "off" ? "Off" : "Default"}
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-white/70">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-white">{formatDate(pkg.created_at)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Update button - show when update is available */}
                        {pkg.update_available && pkg.latest_available_version && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleUpdateToLatest(pkg)}
                            disabled={updatingPackage === pkg.id}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid={`update-btn-${pkg.id}`}
                          >
                            {updatingPackage === pkg.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <ArrowUpCircle className="h-4 w-4 mr-1" />
                            )}
                            Update
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewLogs(pkg)}
                          title="View Logs"
                          data-testid={`logs-btn-${pkg.id}`}
                          className="text-white/90 hover:text-white hover:bg-white/[0.04]"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        {pkg.status === "completed" && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(pkg.id)}
                              title="Download .intunewin"
                              data-testid={`download-btn-${pkg.id}`}
                              className="text-white/90 hover:text-white hover:bg-white/[0.04]"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setUploadPackageId(pkg.id);
                                setShowUploadModal(true);
                              }}
                              title="Upload custom .intunewin"
                              data-testid={`upload-intunewin-btn-${pkg.id}`}
                              className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 font-semibold"
                            >
                              <Upload className="h-4 w-4 text-blue-400" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConfigureDetection(pkg)}
                              title="Configure Detection Rule"
                              data-testid={`configure-detection-btn-${pkg.id}`}
                              className="bg-purple-500 text-white border-purple-600 hover:bg-purple-600 font-semibold"
                            >
                              <Shield className="h-4 w-4 mr-1" />
                              Detection
                            </Button>
                            {pkg.installer_type?.toLowerCase() === 'msi' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConfigureMsiProperties(pkg)}
                                title="Configure MSI Properties"
                                data-testid={`configure-msi-props-btn-${pkg.id}`}
                                className="bg-orange-500 text-white border-orange-600 hover:bg-orange-600 font-semibold"
                              >
                                <Settings className="h-4 w-4 mr-1" />
                                MSI Props
                              </Button>
                            )}
                            {!pkg.intune_app_id && !pkg.is_dependency && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleDeploy(pkg)}
                                title="Deploy to Intune"
                                data-testid={`deploy-btn-${pkg.id}`}
                              >
                                Deploy
                              </Button>
                            )}
                          </>
                        )}
                        {canManageApps && pkg.intune_app_id && pkg.lifecycle !== "retired" && !pkg.is_dependency && !pkg.uninstall_only && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openRetire(pkg)}
                            title="Retire this and older versions from Intune (on the live version: everything older). Removes supersedence and assignments, keeps the apps"
                            className="text-slate-300 hover:text-white hover:bg-white/[0.04]"
                            data-testid={`retire-btn-${pkg.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                        {canManageApps && pkg.lifecycle === "retired" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDeleteIntune(pkg)}
                            title="Delete this retired version's app and unused groups from Intune. ALICE keeps its record."
                            className="text-red-300 border-red-500/60 hover:bg-red-500/20"
                            data-testid={`delete-intune-btn-${pkg.id}`}
                          >
                            Delete from Intune
                          </Button>
                        )}
                        {canManageApps && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletePackageId(pkg.id)}
                            title="Delete"
                            className="text-red-400 hover:text-white hover:bg-red-500 transition-colors"
                            data-testid={`delete-btn-${pkg.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                  return (
                    <Fragment key={parentPkg.id}>
                      {renderPackageRow(parentPkg, false)}
                      {dependenciesExpanded && dependencyPackages.map((dep) => renderPackageRow(dep, true))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-white/90 px-4">
              Page {pagination.page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === totalPages}
              className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </main>

      {/* Logs Modal */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight">
              Package Logs
            </DialogTitle>
            <DialogDescription>
              {selectedPackage?.app_name} - {selectedPackage?.version}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[400px] border border-[rgba(229,239,250,0.1)] rounded-md p-4">
            {loadingLogs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
              </div>
            ) : packageLogs.length === 0 ? (
              <p className="text-center text-white/80 py-8">No logs available</p>
            ) : (
              <div className="space-y-2 font-mono text-sm">
                {packageLogs.map((log) => {
                  // Check if log contains a URL
                  const urlMatch = log.message.match(/(https?:\/\/[^\s]+)/);
                  const hasUrl = urlMatch && log.step === 'download_url';
                  
                  return (
                    <div
                      key={log.id}
                      className={`p-2 border-l-2 rounded-r ${
                        log.level === "error"
                          ? "border-red-500 bg-red-500/10"
                          : log.level === "warning"
                          ? "border-yellow-500 bg-yellow-500/10"
                          : log.step === "download_url"
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-[rgba(229,239,250,0.1)]"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-xs text-white/80 mb-1">
                        <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
                        <span className="uppercase font-medium">{log.step}</span>
                      </div>
                      {hasUrl ? (
                        <div>
                          <p className="text-blue-400 font-medium mb-2">
                            {log.message.split(urlMatch[0])[0]}
                          </p>
                          <a
                            href={urlMatch[0]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 underline break-all font-medium"
                          >
                            {urlMatch[0]}
                          </a>
                          <p className="text-xs text-blue-400 mt-2">
                            Click the link above to download in a new tab
                          </p>
                        </div>
                      ) : (
                        <p className={log.level === "error" ? "text-red-400" : "text-white"}>
                          {log.message}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogsModal(false)} className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletePackageId}
        onOpenChange={(open) => {
          if (!open) {
            setDeletePackageId(null);
            setDeleteIntuneApp(false);
            setDeleteIntuneGroups(false);
          }
        }}
      >
        <AlertDialogContent data-testid="delete-package-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Package</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this package? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 rounded-md border border-white/10 bg-white/5 p-4">
            <p className="text-sm font-medium text-slate-200">
              Keep Intune clean (optional)
            </p>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={deleteIntuneApp}
                onCheckedChange={(v) => setDeleteIntuneApp(!!v)}
                data-testid="delete-intune-app-checkbox"
                disabled={isDeleting}
              />
              <span className="text-sm text-slate-300">
                Also delete the app from{" "}
                <span className="font-semibold text-slate-100">Intune</span>{" "}
                (uses the stored <code>intune_app_id</code>). Supersedence links
                to and from it are removed first. If Intune can't delete it, for
                example because another app depends on it, nothing is deleted.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={deleteIntuneGroups}
                onCheckedChange={(v) => setDeleteIntuneGroups(!!v)}
                data-testid="delete-intune-groups-checkbox"
                disabled={isDeleting}
              />
              <span className="text-sm text-slate-300">
                Also delete associated{" "}
                <span className="font-semibold text-slate-100">
                  Azure AD groups
                </span>{" "}
                (Test group, the rollout stage groups ALICE created, Live and
                Exclusion groups). Named Entra groups used by a deployment
                pipeline are never deleted.
              </span>
            </label>
            {(deleteIntuneApp || deleteIntuneGroups) && (
              <p
                className="text-xs text-amber-300"
                data-testid="delete-intune-warning"
              >
                ⚠ Intune / Azure AD deletions are irreversible and happen in
                the currently selected tenant.
              </p>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="delete-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Retire from Intune (app housekeeping) */}
      <AlertDialog open={!!retirePackage} onOpenChange={(open) => { if (!open && !isRetiring) closeRetire(); }}>
        <AlertDialogContent data-testid="retire-package-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Retire {retirePackage?.app_name}{" "}
              {retirePreview && !(retirePreview.package_ids || []).includes(retirePackage?.id)
                ? `versions older than ${retirePackage?.version}`
                : `${retirePackage?.version} and older`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Removes each version's supersedence links and assignments in Intune, oldest first.
              Older versions are always included, so none is left assigned without the
              supersedence that stops it installing again. The apps stay in Intune, so they can be
              assigned again without a re-upload. Devices that have them installed keep them.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {!retirePreview && !retirePreviewError && (
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading the app's links in Intune…
            </div>
          )}
          {retirePreviewError && (
            <p className="text-sm text-red-300" data-testid="retire-preview-error">{retirePreviewError}</p>
          )}
          {retirePreview?.message && (
            <p className="text-sm text-slate-300" data-testid="retire-preview-message">{retirePreview.message}</p>
          )}
          {retirePreview?.truncated && (
            <p className="text-sm text-amber-300">
              ⚠ Only the oldest {retirePreview.package_ids.length} versions are shown. Retire these,
              then run it again for the rest.
            </p>
          )}
          {(retirePreview?.items || [])[0]?.current_version && (
            <p className="text-sm text-slate-300" data-testid="retire-current-version">
              Current live version:{" "}
              <span className="font-mono text-slate-100">{retirePreview.items[0].current_version}</span>
              {" "}(not changed)
            </p>
          )}
          {(retirePreview?.items || []).map((item) => (
            <div key={item.package_id} className="space-y-2 rounded-md border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
              <p className="font-medium text-slate-100">
                Retire {item.app_name} <span className="font-mono">{item.version}</span>
              </p>
              {item.links && (
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    {item.links.superseded_by.length
                      ? `${item.links.superseded_by
                          .map((p) => `${p.display_name || p.app_id}${p.version ? ` ${p.version}` : ""}`)
                          .join(", ")} will no longer supersede it`
                      : "No other app supersedes it"}
                  </li>
                  <li>
                    {item.links.supersedes.length
                      ? `It will no longer supersede ${item.links.supersedes
                          .map((s) => `${s.display_name || s.app_id}${s.version ? ` ${s.version}` : ""}`)
                          .join(", ")}`
                      : "It supersedes no other app"}
                  </li>
                  <li>
                    {item.assignment_count == null
                      ? "Remove its assignments (count unavailable)"
                      : item.assignment_count
                        ? `Remove its ${item.assignment_count} assignment(s)`
                        : "It has no assignments"}
                  </li>
                  <li>The app stays in Intune, unassigned (use Delete from Intune later to remove it)</li>
                </ul>
              )}
              {item.warnings.map((w) => (
                <p key={w} className="text-amber-300">⚠ {w}</p>
              ))}
              {item.blockers.map((b) => (
                <p key={b} className="text-red-300" data-testid="retire-blocker">✕ {b}</p>
              ))}
            </div>
          ))}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRetiring}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRetire(); }}
              disabled={
                isRetiring ||
                !retirePreview ||
                !(retirePreview.items || []).length ||
                !retirePreview.items.every((i) => i.eligible)
              }
              className="disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="retire-confirm-btn"
            >
              {isRetiring ? "Retiring..." : "Retire"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete a retired version from Intune (app housekeeping) */}
      <AlertDialog open={!!deleteIntunePkg} onOpenChange={(open) => { if (!open && !isDeletingIntune) closeDeleteIntune(); }}>
        <AlertDialogContent data-testid="delete-intune-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete {deleteIntunePkg?.app_name} {deleteIntunePkg?.version} from Intune
            </AlertDialogTitle>
            <AlertDialogDescription>
              Deletes this retired version's app from Intune, and its groups that no other package
              uses. ALICE keeps the package's record, marked deleted, so what was deployed stays on
              file. Devices that have it installed keep it. This can't be undone in Intune.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {!deleteIntunePreview && !deleteIntuneError && (
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking the app's links and groups in Intune…
            </div>
          )}
          {deleteIntuneError && (
            <p className="text-sm text-red-300" data-testid="delete-intune-preview-error">{deleteIntuneError}</p>
          )}
          {(deleteIntunePreview?.items || []).map((item) => (
            <div key={item.package_id} className="space-y-2 rounded-md border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
              <ul className="list-disc pl-5 space-y-1">
                <li>Delete {item.intune_app_ids.length} Intune app{item.intune_app_ids.length === 1 ? "" : "s"}</li>
                <li>
                  {item.groups_to_delete.length
                    ? `Delete ${item.groups_to_delete.length} group(s) only this version used`
                    : "No groups to delete"}
                </li>
                {item.groups_kept.length > 0 && (
                  <li>Keep {item.groups_kept.length} group(s) another package still uses</li>
                )}
              </ul>
              {item.blockers.map((b) => (
                <p key={b} className="text-red-300" data-testid="delete-intune-blocker">✕ {b}</p>
              ))}
            </div>
          ))}
          {deleteIntunePreview && (deleteIntunePreview.items || []).every((i) => i.eligible) && (
            <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-300">
              <Checkbox
                checked={deleteIntuneConfirmed}
                onCheckedChange={(v) => setDeleteIntuneConfirmed(!!v)}
                disabled={isDeletingIntune}
                data-testid="delete-intune-confirm-checkbox"
              />
              I understand the app and groups are deleted from Intune and can't be restored.
            </label>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingIntune}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteIntune(); }}
              disabled={
                isDeletingIntune ||
                !deleteIntuneConfirmed ||
                !deleteIntunePreview ||
                !(deleteIntunePreview.items || []).every((i) => i.eligible)
              }
              className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="delete-intune-confirm-btn"
            >
              {isDeletingIntune ? "Deleting..." : "Delete from Intune"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Check Updates Warning */}
      <AlertDialog open={showUpdateWarning} onOpenChange={setShowUpdateWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-500" />
              Check for Updates
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will check all completed packages against the apps catalog for newer versions.</p>
              <p className="text-orange-400 font-medium">Note: This uses GitHub API which has rate limits. Use sparingly (recommended: once daily).</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCheckUpdates}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Check Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deploy Progress Modal */}
      <Dialog open={showDeployModal} onOpenChange={setShowDeployModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight">
              Deploying to Intune
            </DialogTitle>
            <DialogDescription>
              {deployingPackage?.app_name} - {deployingPackage?.version}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Progress Steps */}
            <div className="space-y-4">
              {/* Wrapping Step */}
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  deployProgress.step === "wrapping" ? "bg-primary text-white" :
                  ["deploying", "complete"].includes(deployProgress.step) ? "bg-green-500 text-white" :
                  "bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/80"
                }`}>
                  {["deploying", "complete"].includes(deployProgress.step) ? (
                    <Check className="h-4 w-4" />
                  ) : deployProgress.step === "wrapping" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <span className="text-sm font-medium">1</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${deployProgress.step === "wrapping" ? "text-cyan-400" : "text-white"}`}>
                    Wrapping
                  </p>
                  <p className="text-xs text-white/80">Creating .intunewin package</p>
                </div>
              </div>

              {/* Deploying Step */}
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  deployProgress.step === "deploying" ? "bg-primary text-white" :
                  deployProgress.step === "complete" ? "bg-green-500 text-white" :
                  "bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/80"
                }`}>
                  {deployProgress.step === "complete" ? (
                    <Check className="h-4 w-4" />
                  ) : deployProgress.step === "deploying" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <span className="text-sm font-medium">2</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${deployProgress.step === "deploying" ? "text-cyan-400" : "text-white"}`}>
                    Deploying
                  </p>
                  <p className="text-xs text-white/80">Uploading to Microsoft Intune</p>
                </div>
              </div>

              {/* Complete Step */}
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  deployProgress.step === "complete" ? "bg-green-500 text-white" :
                  "bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/80"
                }`}>
                  {deployProgress.step === "complete" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <span className="text-sm font-medium">3</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${deployProgress.step === "complete" ? "text-green-400" : "text-white"}`}>
                    Complete
                  </p>
                  <p className="text-xs text-white/80">Deployed successfully</p>
                </div>
              </div>
            </div>

            {/* Status Message */}
            {deployProgress.step === "complete" && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                <Check className="h-5 w-5 text-green-400" />
                <div>
                  <p className="font-medium text-green-300">Deployment Complete</p>
                  <p className="text-sm text-green-400/80">Your application has been deployed to Intune. View it in the Deployments tab.</p>
                </div>
              </div>
            )}

            {deployProgress.step === "failed" && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
                <X className="h-5 w-5 text-red-400" />
                <div>
                  <p className="font-medium text-red-300">Deployment Failed</p>
                  <p className="text-sm text-red-400/80">{deployProgress.error}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeployModal(false)}
              disabled={deployProgress.status === "in_progress"}
              className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
            >
              {deployProgress.step === "complete" ? "Close" : "Cancel"}
            </Button>
            {deployProgress.step === "complete" && (
              <Link to="/deployments">
                <Button className="bg-primary hover:bg-primary/90 text-white">
                  View Deployments
                </Button>
              </Link>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Group Modal */}
      <Dialog open={showTestGroupModal} onOpenChange={setShowTestGroupModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight flex items-center gap-2">
              <Users className="h-5 w-5" />
              Configure Test Group
            </DialogTitle>
            <DialogDescription>
              {deployingPackage?.uninstall_only
                ? `Add up to 5 devices to uninstall ${deployingPackage?.app_name} from first. Only devices can be added — an uninstall aimed at a user group would remove the app from every device they sign into.`
                : `Add up to 5 test users or devices for ${deployingPackage?.app_name}. This group will receive the deployment first for testing.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {testMembers.length > 0 && testMembers[0] !== "" ? (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm">
                <p className="text-green-300">
                  <strong>Previous deployment members loaded.</strong> You can add/remove members below before deploying.
                </p>
              </div>
            ) : (
              <div className={`${deployingPackage?.uninstall_only ? 'bg-red-500/10 border-red-500/30' : 'bg-blue-500/10 border-blue-500/30'} border rounded-lg p-3 text-sm`}>
                <p className={deployingPackage?.uninstall_only ? 'text-red-300' : 'text-blue-300'}>
                  {deployingPackage?.uninstall_only ? (
                    <>
                      <strong>Uninstall only.</strong> Enter device names. These machines will have{" "}
                      {deployingPackage?.app_name} removed — every version found, not just the latest.
                      Once verified you can roll the removal out in phases from the Deployments tab.
                    </>
                  ) : (
                    <>
                      <strong>Note:</strong> Enter user emails or device names that should receive this test deployment. Once verified, you can promote to LIVE deployment from the Deployments tab.
                    </>
                  )}
                </p>
              </div>
            )}
            
            <div className="space-y-3">
              <Label className="text-sm font-medium text-white">
                {deployingPackage?.uninstall_only
                  ? "Devices to uninstall from (max 5)"
                  : "Test Group Members (max 5)"}
              </Label>
              {testMembers.map((member, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder={deployingPackage?.uninstall_only
                      ? `Device name ${index + 1}`
                      : `User email or device name ${index + 1}`}
                    value={member}
                    onChange={(e) => updateTestMember(index, e.target.value)}
                    className="flex-1 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white placeholder:text-white/70"
                    data-testid={`test-member-${index}`}
                  />
                  {testMembers.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTestMember(index)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              {testMembers.length < 5 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addTestMember}
                  className="w-full bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Member
                </Button>
              )}
            </div>
            
            {/* Silent Mode Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)]">
              <div className="flex-1">
                <Label className="text-sm font-medium text-white">Silent Deployment</Label>
                <p className="text-xs text-white/60 mt-0.5">
                  Suppresses all PSADT user prompts during install/uninstall.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={silentMode}
                onClick={() => setSilentMode(!silentMode)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${silentMode ? 'bg-[#006AF2]' : 'bg-white/20'}`}
                data-testid="silent-mode-toggle"
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${silentMode ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestGroupModal(false)} className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
              Cancel
            </Button>
            <Button
              onClick={handleSubmitTestGroup}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {deployingPackage?.uninstall_only ? "Uninstall on Test Devices" : "Deploy to Test Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload .intunewin Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight">
              Upload .intunewin File
            </DialogTitle>
            <DialogDescription>
              Upload a pre-built .intunewin file for this package
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="intunewin-file">Select .intunewin file</Label>
              <Input
                id="intunewin-file"
                type="file"
                accept=".intunewin"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadIntunewin}
              disabled={!uploadFile || uploading}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload .intunewin Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Custom .intunewin File</DialogTitle>
            <DialogDescription>
              Upload a manually created .intunewin file (e.g., created with Microsoft's IntuneWinAppUtil.exe).
              This will replace the auto-generated .intunewin for deployment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="intunewin-file">Select .intunewin File</Label>
              <Input
                id="intunewin-file"
                type="file"
                accept=".intunewin"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                disabled={uploading}
              />
              {uploadFile && (
                <p className="text-sm text-white/80">
                  Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg rounded-md p-3">
              <p className="text-sm text-blue-300">
                <strong>Note:</strong> This will replace the auto-generated .intunewin file. 
                The uploaded file will be used for Intune deployment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setUploadFile(null);
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadIntunewin}
              disabled={!uploadFile || uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detection Rule Review Modal */}
      <Dialog open={showDetectionModal} onOpenChange={(open) => {
        if (!open && !loadingDetectionRule) {
          setShowDetectionModal(false);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Review Before Deploy
            </DialogTitle>
            <DialogDescription>
              Confirm the icon Intune will display for this app and review the detection rule before pushing to your test group.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* ===== App Icon Preview & Override ===== */}
            <div
              className="rounded-lg border border-white/10 bg-white/5 p-4"
              data-testid="deploy-icon-preview-section"
            >
              <div className="flex items-start gap-4">
                <div
                  className="h-20 w-20 rounded-md bg-white/10 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0"
                  data-testid="deploy-icon-preview-thumb"
                >
                  {iconLoading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-white/50" />
                  ) : iconPreview?.icon_base64 ? (
                    <img
                      src={`data:image/png;base64,${iconPreview.icon_base64}`}
                      alt="App icon Intune will display"
                      className="h-full w-full object-contain"
                      data-testid="deploy-icon-preview-img"
                    />
                  ) : (
                    <Package className="h-8 w-8 text-white/40" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium">App icon</p>
                    {iconPreview?.has_override ? (
                      <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30 text-[10px]" data-testid="deploy-icon-source-badge">
                        Custom upload
                      </Badge>
                    ) : iconPreview?.source === "family_reused" ? (
                      <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30 text-[10px]" data-testid="deploy-icon-source-badge">
                        Reused
                      </Badge>
                    ) : iconPreview?.icon_base64 ? (
                      <Badge className="bg-blue-500/20 text-blue-200 border-blue-400/30 text-[10px]" data-testid="deploy-icon-source-badge">
                        Auto-detected
                      </Badge>
                    ) : !iconLoading ? (
                      <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 text-[10px]" data-testid="deploy-icon-source-badge">
                        Missing
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-xs text-white/70 mt-1" data-testid="deploy-icon-source-label">
                    {iconLoading
                      ? "Loading preview..."
                      : iconPreview?.source_label || "—"}
                  </p>
                  <p className="text-[11px] text-white/50 mt-2">
                    PNG or JPG, max 1 MB. Uploads are auto-resized to 256×256 and kept for every later version of this app.
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <label
                      htmlFor="deploy-icon-file-input"
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
                        iconUploading
                          ? "bg-white/10 text-white/40 cursor-not-allowed"
                          : "bg-white/10 hover:bg-white/20 text-white"
                      }`}
                      data-testid="deploy-icon-upload-btn"
                    >
                      {iconUploading ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-3.5 w-3.5" />
                          {iconPreview?.has_override ? "Replace icon" : "Upload custom icon"}
                        </>
                      )}
                      <input
                        id="deploy-icon-file-input"
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        disabled={iconUploading || iconResetting}
                        onChange={handleIconFileSelected}
                        data-testid="deploy-icon-file-input"
                      />
                    </label>
                    {(iconPreview?.has_override || iconPreview?.can_reset) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        disabled={iconResetting || iconUploading}
                        onClick={handleIconReset}
                        data-testid="deploy-icon-reset-btn"
                      >
                        {iconResetting ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            Resetting...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            Reset to auto
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Detection Rule Info Banner */}
            {detectionRule?.tier === 1 ? (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-900 text-sm">Tier 1 - High Confidence Detection</p>
                    <p className="text-sm text-green-700 mt-1">
                      This rule was auto-generated from the winget manifest's Add/Remove Programs data. It's the most stable detection method for EXE installers.
                    </p>
                  </div>
                </div>
              </div>
            ) : detectionRule?.tier === 2 ? (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-900 text-sm">Tier 2 - Script-Based Detection</p>
                    <p className="text-sm text-blue-700 mt-1">
                      This PowerShell script searches registry for the application. Review the script and modify if needed.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-900 text-sm">Auto-generated Rule - Review Required</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This rule was automatically generated based on typical installation patterns. Please verify it matches where your application actually installs.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Detection Type Selector */}
            <div className="space-y-2">
              <Label>Detection Method</Label>
              <Select value={detectionRuleType} onValueChange={setDetectionRuleType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="registry">
                    <div className="flex items-center gap-2">
                      <span>📋 Registry Detection (ARP)</span>
                      {detectionRule?.tier === 1 && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Tier 1 - Recommended</span>}
                    </div>
                  </SelectItem>
                  <SelectItem value="script">
                    <div className="flex items-center gap-2">
                      <span>📜 PowerShell Script Detection</span>
                      {detectionRule?.tier === 2 && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Tier 2</span>}
                    </div>
                  </SelectItem>
                  <SelectItem value="msi">MSI Product Code Detection</SelectItem>
                  <SelectItem value="file">
                    <div className="flex items-center gap-2">
                      <span>📁 File or Folder Detection</span>
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">Tier 3 - Fallback</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-white/90">
                {detectionRuleType === "registry" 
                  ? "Searches Add/Remove Programs registry for the application (most stable for EXE installers)"
                  : detectionRuleType === "script"
                  ? "Uses a PowerShell script to detect installation (flexible, handles edge cases)"
                  : detectionRuleType === "msi"
                  ? "Checks for an installed MSI using its Product Code"
                  : "Checks for the existence of a file or folder (fallback method)"}
              </p>
            </div>

            {/* File Detection Fields */}
            {detectionRuleType === "file" && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label htmlFor="file-path">
                    Installation Path <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="file-path"
                    value={detectionRule?.path || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), path: e.target.value})}
                    placeholder="%ProgramFiles%\Publisher\AppName"
                  />
                  <p className="text-xs text-white/90">
                    Use environment variables like %ProgramFiles%, %ProgramFiles(x86)%, or %ProgramData%
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file-name">
                    File or Folder Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="file-name"
                    value={detectionRule?.fileName || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), fileName: e.target.value})}
                    placeholder="AppName.exe"
                  />
                  <p className="text-xs text-white/90">
                    The file or folder to check for (e.g., "app.exe" or just the folder name)
                  </p>
                </div>

                {/* Detection Method */}
                <div className="space-y-2">
                  <Label htmlFor="detection-method">Detection Method</Label>
                  <Select 
                    value={detectionRule?.detectionType || "exists"} 
                    onValueChange={(value) => {
                      const currentRule = detectionRule || {};
                      setDetectionRule({
                        ...currentRule, 
                        detectionType: value,
                        operator: value === 'exists' ? undefined : 'equal',
                        comparisonValue: value === 'exists' ? undefined : ''
                      });
                    }}
                  >
                    <SelectTrigger id="detection-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exists">File or folder exists</SelectItem>
                      <SelectItem value="version">String (version)</SelectItem>
                      <SelectItem value="size">Size</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-white/90">
                    Choose how to detect the application
                  </p>
                </div>

                {/* Operator and Value (only show for version/size) */}
                {(detectionRule?.detectionType === 'version' || detectionRule?.detectionType === 'size') && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="operator">Operator</Label>
                        <Select 
                          value={detectionRule?.operator || "equal"} 
                          onValueChange={(value) => setDetectionRule({...(detectionRule || {}), operator: value})}
                        >
                          <SelectTrigger id="operator">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equal">Equals</SelectItem>
                            <SelectItem value="notEqual">Not equal to</SelectItem>
                            <SelectItem value="greaterThan">Greater than</SelectItem>
                            <SelectItem value="greaterThanOrEqual">Greater than or equal to</SelectItem>
                            <SelectItem value="lessThan">Less than</SelectItem>
                            <SelectItem value="lessThanOrEqual">Less than or equal to</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="comparison-value">
                          {detectionRule?.detectionType === 'version' ? 'Version' : 'Size (bytes)'}
                        </Label>
                        <Input
                          id="comparison-value"
                          value={detectionRule?.comparisonValue || ""}
                          onChange={(e) => setDetectionRule({...(detectionRule || {}), comparisonValue: e.target.value})}
                          placeholder={detectionRule?.detectionType === 'version' ? '25.1.0.0' : '1024000'}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-white/90">
                      {detectionRule?.detectionType === 'version' 
                        ? 'Enter the version number to compare (e.g., 25.1.0.0)'
                        : 'Enter the file size in bytes to compare'}
                    </p>
                  </>
                )}

                {/* 32-bit check option */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="check32bit"
                    checked={detectionRule?.check32BitOn64System || false}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), check32BitOn64System: e.target.checked})}
                    className="h-4 w-4 rounded border-white/15"
                  />
                  <Label htmlFor="check32bit" className="text-sm font-normal cursor-pointer">
                    Check for 32-bit on 64-bit systems
                  </Label>
                </div>

                {/* Common Templates */}
                <div className="space-y-2">
                  <Label>Quick Templates</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const appName = deployingPackage?.app_name?.replace(/[^A-Za-z0-9_\- ]/g, '') || 'App';
                        setDetectionRule({
                          ...(detectionRule || {}),
                          path: `%ProgramFiles%\\${appName}`,
                          fileName: 'Deploy-Application.exe'
                        });
                      }}
                    >
                      Program Files
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const appName = deployingPackage?.app_name?.replace(/[^A-Za-z0-9_\- ]/g, '') || 'App';
                        setDetectionRule({
                          ...(detectionRule || {}),
                          path: `%ProgramFiles(x86)%\\${appName}`,
                          fileName: 'Deploy-Application.exe'
                        });
                      }}
                    >
                      Program Files (x86)
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const appName = deployingPackage?.app_name?.replace(/[^A-Za-z0-9_\- ]/g, '') || 'App';
                        setDetectionRule({
                          ...(detectionRule || {}),
                          path: `%ProgramData%\\${appName}`,
                          fileName: 'Deploy-Application.exe'
                        });
                      }}
                    >
                      ProgramData
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const appName = deployingPackage?.app_name?.replace(/[^A-Za-z0-9_\- ]/g, '') || 'App';
                        setDetectionRule({
                          ...(detectionRule || {}),
                          path: `%LocalAppData%\\${appName}`,
                          fileName: 'Deploy-Application.exe'
                        });
                      }}
                    >
                      LocalAppData
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Registry (ARP) Detection Fields - Tier 1 */}
            {detectionRuleType === "registry" && (
              <div className="space-y-4 border-t pt-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 rounded-lg">
                  <p className="text-sm text-green-300">
                    <strong>Tier 1 - Registry Detection (Recommended)</strong><br/>
                    This method searches the Add/Remove Programs registry for the application. It's the most stable detection method for EXE installers.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="display-name">
                    Display Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="display-name"
                    value={detectionRule?.displayName || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), displayName: e.target.value})}
                    placeholder="Application Name"
                  />
                  <p className="text-xs text-white/90">
                    The name that appears in Add/Remove Programs (supports partial matching)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="display-version">Display Version</Label>
                  <Input
                    id="display-version"
                    value={detectionRule?.displayVersion || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), displayVersion: e.target.value})}
                    placeholder="1.0.0"
                  />
                  <p className="text-xs text-white/90">
                    Minimum version to consider installed
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="version-comparison">Version Comparison</Label>
                  <Select 
                    value={detectionRule?.versionComparison || "greaterThanOrEqual"} 
                    onValueChange={(value) => setDetectionRule({...(detectionRule || {}), versionComparison: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="greaterThanOrEqual">Greater Than or Equal (≥)</SelectItem>
                      <SelectItem value="greaterThan">Greater Than (&gt;)</SelectItem>
                      <SelectItem value="exact">Exact Match (=)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="registry-publisher">Publisher (Optional)</Label>
                  <Input
                    id="registry-publisher"
                    value={detectionRule?.publisher || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), publisher: e.target.value})}
                    placeholder="Publisher Name"
                  />
                  <p className="text-xs text-white/90">
                    Optional: Filter by publisher name
                  </p>
                </div>
              </div>
            )}

            {/* PowerShell Script Detection Fields - Tier 2 */}
            {detectionRuleType === "script" && (
              <div className="space-y-4 border-t pt-4">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 rounded-lg">
                  <p className="text-sm text-blue-300">
                    <strong>Tier 2 - PowerShell Script Detection</strong><br/>
                    This script searches registry uninstall keys across all hives. It handles per-user installations and edge cases.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="script-content">
                    Detection Script <span className="text-red-500">*</span>
                  </Label>
                  <textarea
                    id="script-content"
                    value={detectionRule?.scriptContent || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), scriptContent: e.target.value})}
                    placeholder="# PowerShell detection script..."
                    className="w-full h-64 p-3 font-mono text-sm bg-black/40 text-green-400 border border-white/20 rounded-md resize-y"
                  />
                  <p className="text-xs text-white/90">
                    Script must exit 0 with stdout output if installed, or exit 1 if not installed
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Script Info</Label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-white/90">Searches for:</span>
                      <span className="ml-2 font-medium">{detectionRule?.displayName || deployingPackage?.app_name}</span>
                    </div>
                    <div>
                      <span className="text-white/90">Version:</span>
                      <span className="ml-2 font-medium">≥ {detectionRule?.displayVersion || deployingPackage?.version}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MSI Detection Fields */}
            {detectionRuleType === "msi" && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label htmlFor="product-code">
                    MSI Product Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="product-code"
                    value={detectionRule?.productCode || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), productCode: e.target.value})}
                    placeholder="{XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}"
                  />
                  <p className="text-xs text-white/90">
                    The MSI Product Code (GUID) from the installer. Find it using tools like Orca or PowerShell.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product-version">Product Version (Optional)</Label>
                  <Input
                    id="product-version"
                    value={detectionRule?.productVersion || ""}
                    onChange={(e) => setDetectionRule({...(detectionRule || {}), productVersion: e.target.value})}
                    placeholder="1.0.0.0"
                  />
                  <p className="text-xs text-white/90">
                    Optional: Specify a minimum version if needed
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetectionModal(false)}
              disabled={loadingDetectionRule}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDetectionRuleConfirm}
              disabled={loadingDetectionRule}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {loadingDetectionRule ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm & Continue
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MSI Properties Modal */}
      <Dialog open={showMsiPropsModal} onOpenChange={(open) => {
        if (!open) {
          setShowMsiPropsModal(false);
          setMsiProperties(null);
          setEditedMsiProps({});
          setMsiExpertMode(false);
          setMstMode(false);
          setMstPreview(null);
        }
      }}>
        <DialogContent className="sm:max-w-[800px] max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-orange-600" />
              MSI Properties - {msiPropsPackage?.app_name}
            </DialogTitle>
            <DialogDescription>
              Configure MSI installation properties for enterprise deployment.
            </DialogDescription>
          </DialogHeader>

          {loadingMsiProps ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
            </div>
          ) : msiProperties ? (
            <ScrollArea className="max-h-[550px] pr-4">
              <div className="space-y-6">
                {/* Product Info Section */}
                {msiProperties.product_info && Object.keys(msiProperties.product_info).length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-white/90 uppercase tracking-wide">
                      Product Information (Read-Only)
                    </h4>
                    <div className="bg-white/10 border rounded-lg p-4 space-y-2">
                      {Object.entries(msiProperties.product_info).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-white/90">{key}:</span>
                          <span className="font-mono text-xs text-foreground">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expert Mode Toggle Card */}
                <div className="border rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/50 cursor-pointer hover:from-slate-100 hover:to-slate-200 dark:hover:from-slate-800 dark:hover:to-slate-900 transition-colors"
                    onClick={() => setMsiExpertMode(!msiExpertMode)}
                    data-testid="msi-expert-mode-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${msiExpertMode ? 'bg-orange-500/20' : 'bg-white/20'}`}>
                        <Settings className={`h-5 w-5 ${msiExpertMode ? 'text-orange-600' : 'text-white/70'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm">MSI Properties</h4>
                        <p className="text-xs text-white/90">
                          {msiProperties.stats?.total || 0} total
                          {msiProperties.stats?.public > 0 && <> • <span className="text-green-600 font-medium">{msiProperties.stats.public} public</span></>}
                          {msiProperties.stats?.secure_custom > 0 && <> • <span className="text-blue-600 font-medium">{msiProperties.stats.secure_custom} app config</span></>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-white/90">Expert Mode</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={msiExpertMode}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          msiExpertMode ? 'bg-orange-600' : 'bg-white/30'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMsiExpertMode(!msiExpertMode);
                        }}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            msiExpertMode ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expert Mode Content - Collapsible */}
                  {msiExpertMode && (
                    <div className="p-4 border-t space-y-6 bg-white/10">
                      <p className="text-xs text-white/90">
                        Only PUBLIC properties (ALL UPPERCASE) can be passed via msiexec command line.
                      </p>

                {/* Secure Custom Properties Section (App-specific config like server URLs) */}
                {msiProperties.secure_custom_properties && msiProperties.secure_custom_properties.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-orange-600 uppercase tracking-wide flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Application Configuration (SecureCustomProperties)
                    </h4>
                    <p className="text-xs text-white/90">
                      These properties are defined by the application for custom configuration (e.g., server URLs, license keys).
                    </p>
                    <div className="space-y-2">
                      {msiProperties.secure_custom_properties.map((prop) => (
                        <div key={prop.name} className="flex items-center justify-between p-3 border border-orange-200 bg-orange-500/10 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Label className="font-mono text-sm font-medium">{prop.name}</Label>
                              <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 border-orange-300">
                                Custom
                              </Badge>
                            </div>
                            <p className="text-xs text-white/90 mt-1">
                              {prop.description || "Application-specific configuration"}
                            </p>
                            <p className="text-xs text-white/90 font-mono mt-1">
                              Default: {prop.value || "(empty)"}
                            </p>
                          </div>
                          <div className="ml-4 w-[200px]">
                            <Input
                              value={editedMsiProps[prop.name] ?? prop.value ?? ""}
                              onChange={(e) => updateMsiPropertyValue(prop.name, e.target.value)}
                              placeholder={prop.value || "Enter value..."}
                              className="font-mono text-sm"
                              data-testid={`msi-prop-${prop.name}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Public Properties Table */}
                {msiProperties.public_properties && msiProperties.public_properties.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-white/90 uppercase tracking-wide">
                      Public Properties ({msiProperties.public_properties.length})
                    </h4>
                    <p className="text-xs text-white/90">
                      PUBLIC properties can be overridden via msiexec command line. Toggle or edit values below.
                    </p>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-white/10">
                            <TableHead className="w-[180px]">Property</TableHead>
                            <TableHead className="w-[150px]">Default Value</TableHead>
                            <TableHead>Override Value</TableHead>
                            <TableHead className="w-[100px]">Category</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {msiProperties.public_properties
                            .filter(prop => !prop.is_secure_custom && prop.editable)
                            .slice(0, 30) // Limit display for performance
                            .map((prop) => (
                            <TableRow key={prop.name} className={prop.recommended ? "bg-green-500/10" : ""}>
                              <TableCell className="font-mono text-sm">
                                <div className="flex items-center gap-2">
                                  {prop.name}
                                  {prop.recommended && (
                                    <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                                      Recommended
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-xs text-white/90 truncate max-w-[150px]" title={prop.value}>
                                {prop.value || "(empty)"}
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={editedMsiProps[prop.name] ?? ""}
                                  onChange={(e) => {
                                    if (e.target.value === "") {
                                      const newProps = { ...editedMsiProps };
                                      delete newProps[prop.name];
                                      setEditedMsiProps(newProps);
                                    } else {
                                      updateMsiPropertyValue(prop.name, e.target.value);
                                    }
                                  }}
                                  placeholder={prop.value || "Enter override..."}
                                  className="font-mono text-sm h-8"
                                  data-testid={`msi-prop-${prop.name}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="text-xs">
                                  {prop.category}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {msiProperties.public_properties.filter(p => !p.is_secure_custom && p.editable).length > 30 && (
                      <p className="text-xs text-white/90 text-center">
                        Showing first 30 properties. See "All MSI Properties" below for complete list.
                      </p>
                    )}
                  </div>
                )}

                {/* Common Deployment Properties Quick Toggle */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-white/90 uppercase tracking-wide">
                    Quick Settings
                  </h4>
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-medium">ALLUSERS</Label>
                        <p className="text-xs text-white/90">Install for all users (1) or current user only (blank)</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={editedMsiProps.ALLUSERS ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleMsiProperty('ALLUSERS', '1')}
                        >
                          {editedMsiProps.ALLUSERS ? <Check className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                          {editedMsiProps.ALLUSERS ? 'Enabled' : 'Disabled'}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-medium">REBOOT</Label>
                        <p className="text-xs text-white/90">Suppress reboot prompts during installation</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={editedMsiProps.REBOOT || "none"} 
                          onValueChange={(value) => {
                            if (value === "none") {
                              const newProps = { ...editedMsiProps };
                              delete newProps.REBOOT;
                              setEditedMsiProps(newProps);
                            } else {
                              updateMsiPropertyValue('REBOOT', value);
                            }
                          }}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Not Set</SelectItem>
                            <SelectItem value="ReallySuppress">ReallySuppress</SelectItem>
                            <SelectItem value="Suppress">Suppress</SelectItem>
                            <SelectItem value="Force">Force</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* All MSI Properties Section (Expandable) */}
                {msiProperties.all_properties && msiProperties.all_properties.length > 0 && (
                  <div className="space-y-3">
                    <details className="group">
                      <summary className="cursor-pointer font-semibold text-sm text-white/90 uppercase tracking-wide flex items-center gap-2">
                        <span>All MSI Properties ({msiProperties.all_properties.length})</span>
                        <ChevronRight className="h-4 w-4 group-open:rotate-90 transition-transform" />
                        {msiProperties.stats && (
                          <span className="text-xs font-normal ml-2">
                            ({msiProperties.stats.public || 0} public, {msiProperties.stats.private || 0} private)
                          </span>
                        )}
                      </summary>
                      <div className="mt-3 bg-white/10 rounded-lg p-3 max-h-[250px] overflow-y-auto border">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-1 px-2 text-foreground font-semibold">Property</th>
                              <th className="text-left py-1 px-2 text-foreground font-semibold">Value</th>
                              <th className="text-left py-1 px-2 text-foreground font-semibold">Type</th>
                              <th className="text-left py-1 px-2 text-foreground font-semibold">Editable</th>
                            </tr>
                          </thead>
                          <tbody>
                            {msiProperties.all_properties.map((prop, idx) => (
                              <tr key={idx} className={`border-b border-border/50 ${prop.is_secure_custom ? 'bg-orange-500/10' : ''}`}>
                                <td className="py-1 px-2 font-mono text-foreground">
                                  {prop.name}
                                  {prop.is_secure_custom && <span className="ml-1 text-orange-600">*</span>}
                                </td>
                                <td className="py-1 px-2 font-mono text-white/90 truncate max-w-[200px]" title={prop.value}>
                                  {prop.value}
                                </td>
                                <td className="py-1 px-2">
                                  <Badge variant={prop.is_public ? "default" : "secondary"} className="text-xs">
                                    {prop.category}
                                  </Badge>
                                </td>
                                <td className="py-1 px-2">
                                  {prop.editable ? (
                                    <Check className="h-3 w-3 text-green-600" />
                                  ) : (
                                    <X className="h-3 w-3 text-red-400" />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-white/90 mt-2">
                        * Properties from SecureCustomProperties (app-specific configuration)
                      </p>
                    </details>
                  </div>
                )}
                    </div>
                  )}
                </div>

                {/* Preview of command line */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-white/90 uppercase tracking-wide">
                    Command Preview
                  </h4>
                  <div className="bg-slate-900 text-green-400 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                    Start-ADTMsiProcess -Action 'Install' -FilePath "$($adtSession.DirFiles)\{msiProperties.msi_filename}" 
                    {Object.keys(editedMsiProps).length > 0 && !mstMode && (
                      <span className="text-yellow-400">
                        {' '}-AdditionalArgumentList '{Object.entries(editedMsiProps).map(([k, v]) => `${k}=${v}`).join(' ')}'
                      </span>
                    )}
                    {mstMode && (
                      <span className="text-cyan-400">
                        {' '}-Transforms "$($adtSession.DirFiles)\alice.mst"
                      </span>
                    )}
                  </div>
                </div>

                {/* MST Mode — bake edits into an MSI Transform instead of CLI args */}
                <div className="space-y-3 border rounded-lg p-4 bg-cyan-500/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm text-cyan-400 uppercase tracking-wide flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        MSI Transform (MST) Mode
                        <Badge variant="outline" className="text-[10px] bg-yellow-500/10 text-yellow-500 border-yellow-500/40">Phase 1 Preview</Badge>
                      </h4>
                      <p className="text-xs text-white/70 mt-1">
                        Bake the property overrides above into a generated <code>.mst</code> file, applied via
                        <code className="mx-1">msiexec /i base.msi TRANSFORMS=alice.mst</code>. Preserves the vendor MSI byte-for-byte.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant={mstMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMstMode(!mstMode)}
                        className={mstMode ? "bg-cyan-600 hover:bg-cyan-700 text-white" : ""}
                        data-testid="mst-mode-toggle"
                      >
                        {mstMode ? (<><Check className="h-4 w-4 mr-1" /> MST enabled</>) : (<><Plus className="h-4 w-4 mr-1" /> Use MST</>)}
                      </Button>
                    </div>
                  </div>

                  {mstMode && (
                    <div className="space-y-3">
                      {/* Preview area */}
                      {mstPreview ? (
                        <div className="bg-slate-900/80 rounded-lg p-3 space-y-2 text-xs">
                          <div className="flex justify-between text-white/70">
                            <span>Base MSI</span>
                            <span className="font-mono">{mstPreview.base_msi_filename}</span>
                          </div>
                          <div className="flex justify-between text-white/70">
                            <span>ProductCode</span>
                            <span className="font-mono">{mstPreview.base_product_code}</span>
                          </div>
                          <div className="flex justify-between text-white/70">
                            <span>PackageCode</span>
                            <span className="font-mono">{mstPreview.base_package_code}</span>
                          </div>
                          <div className="border-t border-white/10 my-2"></div>
                          <div className="text-white font-semibold">
                            {mstPreview.change_count} change{mstPreview.change_count === 1 ? "" : "s"} the MST will apply:
                          </div>
                          {mstPreview.changes.length === 0 ? (
                            <div className="text-white/50 italic">No overrides differ from base MSI. MST would be empty — add property overrides above.</div>
                          ) : (
                            <div className="space-y-1 font-mono">
                              {mstPreview.changes.map((c, i) => (
                                <div key={i} className="flex items-center gap-2" data-testid={`mst-change-${c.name}`}>
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] shrink-0 ${
                                      c.kind === "update"
                                        ? "border-yellow-500/40 text-yellow-400 bg-yellow-500/10"
                                        : "border-green-500/40 text-green-400 bg-green-500/10"
                                    }`}
                                  >
                                    {c.kind}
                                  </Badge>
                                  <span className="text-cyan-300">{c.name}</span>
                                  {c.kind === "update" && (
                                    <>
                                      <span className="text-white/50">:</span>
                                      <span className="text-red-400 line-through">{c.old || '""'}</span>
                                      <span className="text-white/50">→</span>
                                    </>
                                  )}
                                  {c.kind === "insert" && <span className="text-white/50">=</span>}
                                  <span className="text-green-400">{c.new || '""'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : loadingMstPreview ? (
                        <div className="flex items-center justify-center py-4 text-white/60 text-xs">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading MST preview...
                        </div>
                      ) : (
                        <div className="text-xs text-white/60 italic">
                          Save property overrides first, then re-open this dialog to preview the MST.
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadMstPreview(msiPropsPackage?.id)}
                          disabled={loadingMstPreview || !msiPropsPackage?.id}
                          data-testid="mst-refresh-preview-btn"
                        >
                          {loadingMstPreview ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                          Refresh preview
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => downloadMst(msiPropsPackage?.id, msiPropsPackage?.app_name)}
                          disabled={!mstPreview || mstPreview.change_count === 0 || downloadingMst}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white"
                          data-testid="mst-download-btn"
                        >
                          {downloadingMst ? (
                            <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Building MST...</>
                          ) : (
                            <><Download className="h-3 w-3 mr-1" /> Download .mst</>
                          )}
                        </Button>
                      </div>

                      <div className="text-[10px] text-white/50 italic border-t border-white/10 pt-2">
                        Phase 1 caveat: generated on Linux without a Windows validation oracle. First install on your test
                        device — verify <code>install.log</code> shows the property override applied, then roll to production.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-white/90">
              Failed to load MSI properties
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMsiPropsModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveMsiProperties}
              disabled={savingMsiProps || loadingMsiProps}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {savingMsiProps ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Properties
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Description Preview / Edit Modal (wizard step before Supersede) */}
      <Dialog open={showDescriptionModal} onOpenChange={(open) => {
        if (!open && !descriptionSaving) setShowDescriptionModal(false);
      }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight flex items-center gap-2">
              <FileText className="h-5 w-5 text-cyan-400" />
              Review Intune Description
            </DialogTitle>
            <DialogDescription>
              This is the description Intune will display for{" "}
              <strong>{deployingPackage?.app_name}</strong>. Edit it now if you'd
              like; the changes are used on the next deploy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <Label htmlFor="intune-description" className="text-sm text-white">
              Description
            </Label>
            {descriptionLoading ? (
              <div className="flex items-center gap-2 text-sm text-white/70 py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading auto-generated description…
              </div>
            ) : (
              <>
                <Textarea
                  id="intune-description"
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  rows={10}
                  maxLength={4000}
                  className="font-mono text-xs bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white"
                  data-testid="intune-description-textarea"
                />
                <p className="text-[11px] text-white/60 text-right">
                  {(descriptionDraft || "").length} / 4000 characters
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDescriptionModal(false)}
              disabled={descriptionSaving}
              className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
              data-testid="intune-description-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDescriptionConfirm}
              disabled={descriptionSaving || descriptionLoading}
              className="bg-primary hover:bg-primary/90 text-white"
              data-testid="intune-description-confirm-btn"
            >
              {descriptionSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save & Continue
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supersede Apps Modal */}
      <SupersedeAppsModal
        open={showSupersedeModal}
        onOpenChange={setShowSupersedeModal}
        packageId={deployingPackage?.id}
        packageName={deployingPackage?.app_name}
        deploymentType={supersedeDeploymentType}
        onConfirm={handleSupersedeConfirm}
        getAuthHeaders={getAuthHeaders}
        API={API}
      />
    </div>
  );
}
               
